import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { api, Connection, Post, XCostEstimate } from '../api/client'
import { useWorkspaceContext } from '../hooks/useWorkspaceContext'
import AIPromptInput, { type AIPromptInputValues } from '../components/AIPromptInput'

function getPlatformPostUrl(platformName?: string, platformPostID?: string, displayName?: string): string | null {
  if (!platformPostID || !platformName) return null
  switch (platformName) {
    case 'Bluesky': {
      if (!platformPostID.startsWith('at://')) return null
      const parts = platformPostID.replace('at://', '').split('/')
      if (parts.length < 3) return null
      return `https://bsky.app/profile/${parts[0]}/post/${parts[2]}`
    }
    case 'Facebook':
      return `https://www.facebook.com/${platformPostID}`
    case 'X/Twitter':
      return `https://x.com/i/status/${platformPostID}`
    case 'LinkedIn Org':
    case 'LinkedIn Personal':
      return platformPostID.startsWith('urn:')
        ? `https://www.linkedin.com/feed/update/${platformPostID}/`
        : `https://www.linkedin.com/feed/update/urn:li:share:${platformPostID}/`
    case 'Pinterest':
      return `https://www.pinterest.com/pin/${platformPostID}/`
    case 'Threads': {
      const h = displayName?.match(/@([\w.]+)/)
      return h ? `https://www.threads.net/@${h[1]}` : null
    }
    case 'Instagram': {
      const h = displayName?.match(/@([\w.]+)/)
      return h ? `https://www.instagram.com/${h[1]}/` : null
    }
    case 'TikTok':
      return displayName ? `https://www.tiktok.com/@${displayName}` : null
    case 'YouTube':
      return platformPostID ? `https://www.youtube.com/watch?v=${platformPostID}` : null
    default: return null
  }
}

interface AttachedMedia { mediaID: number; fileName: string; blobUrl: string; mimeType: string }

const PLATFORM_CHAR_LIMITS: Record<string, number> = {
  'X/Twitter': 280, 'LinkedIn': 3000, 'Facebook': 63206,
  'Instagram': 2200, 'Bluesky': 300, 'TikTok': 2200,
  'YouTube': 5000, 'Google Business': 1500,
  'Pinterest': 500, 'Telegram': 4096, 'Discord': 2000,
  'Threads': 500,
}

const EMOJI_PICKS = ['😀','😂','🔥','❤️','👏','🎉','🎵','🎶','🎸','🎤','🎧','💯','✨','🚀','💪','🙌','👀','🎯','💡','🌟']

const PLATFORM_IMAGE_LIMITS: Record<string, number> = {
  'X/Twitter': 4, 'Bluesky': 4, 'LinkedIn': 9,
  'Facebook': 10, 'Instagram': 10, 'Threads': 10,
  'TikTok': 1, 'YouTube': 1, 'Google Business': 1,
  'Pinterest': 1, 'Telegram': 10,
}

const PLATFORM_MEDIA_SUPPORT: Record<string, { video: string[]; image: string[] }> = {
  'TikTok':           { video: ['video/mp4','video/quicktime'], image: ['image/jpeg','image/webp'] },
  'YouTube':          { video: ['video/mp4','video/quicktime','video/webm','video/x-msvideo','video/x-ms-wmv','video/x-flv','video/3gpp','video/mpeg','video/x-matroska','video/mp2t'], image: [] },
  'Instagram':        { video: ['video/mp4','video/quicktime'], image: ['image/jpeg','image/png'] },
  'Facebook':         { video: ['video/mp4','video/quicktime','video/x-msvideo','video/x-ms-wmv','video/x-matroska','video/webm','video/3gpp','video/x-flv','video/mpeg'], image: ['image/jpeg','image/png','image/gif','image/webp','image/bmp'] },
  'X/Twitter':        { video: ['video/mp4','video/quicktime'], image: ['image/jpeg','image/png','image/gif','image/webp'] },
  'Bluesky':          { video: ['video/mp4'], image: ['image/jpeg','image/png'] },
  'LinkedIn':         { video: ['video/mp4','video/quicktime'], image: ['image/jpeg','image/png','image/gif'] },
  'Telegram':         { video: ['video/mp4','video/quicktime','video/x-msvideo','video/webm','video/x-matroska','video/x-flv','video/mpeg'], image: ['image/jpeg','image/png','image/gif','image/webp','image/bmp'] },
  'Discord':          { video: ['video/mp4','video/quicktime','video/webm'], image: ['image/jpeg','image/png','image/gif','image/webp'] },
  'Google Business':  { video: [], image: ['image/jpeg','image/png'] },
  'Pinterest':        { video: ['video/mp4','video/quicktime'], image: ['image/jpeg','image/png','image/webp'] },
  'Threads':          { video: ['video/mp4','video/quicktime'], image: ['image/jpeg','image/png'] },
}

function getFormatWarnings(media: AttachedMedia[], platformName: string): string[] {
  const support = PLATFORM_MEDIA_SUPPORT[platformName]
  if (!support) return []
  const warnings: string[] = []
  for (const m of media) {
    const isVideo = m.mimeType.startsWith('video/')
    const isImage = m.mimeType.startsWith('image/')
    const allowed = isVideo ? support.video : isImage ? support.image : []
    if (isVideo && support.video.length === 0) {
      warnings.push(`${platformName} does not support video`)
      break
    }
    if (isVideo && allowed.length > 0 && !allowed.includes(m.mimeType)) {
      const ext = m.fileName.split('.').pop()?.toUpperCase() || m.mimeType
      warnings.push(`${ext} will be auto-converted to MP4 for ${platformName}`)
      continue
    }
    if (isImage && allowed.length > 0 && !allowed.includes(m.mimeType)) {
      const ext = m.fileName.split('.').pop()?.toUpperCase() || m.mimeType
      if (m.mimeType === 'image/gif') {
        warnings.push(`GIF will be auto-converted to JPEG for ${platformName} -- animation will be lost (first frame only)`)
      } else if (m.mimeType === 'image/webp' || m.mimeType === 'image/bmp') {
        warnings.push(`${ext} will be auto-converted to JPEG for ${platformName}`)
      } else {
        warnings.push(`${ext} not supported by ${platformName}`)
      }
    }
  }
  return warnings
}

function parseSuggestions(raw: string): { text: string; angle?: string }[] {
  // Try JSON first — backend asks for JSON array
  let cleaned = raw.trim()
  // Strip markdown code fences if present
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '')
  }
  try {
    const arr = JSON.parse(cleaned)
    if (Array.isArray(arr) && arr.length > 0) {
      return arr.map((item: unknown) => {
        if (typeof item === 'string') return { text: item }
        const obj = item as Record<string, unknown>
        return { text: String(obj.text || obj.content || ''), angle: obj.angle ? String(obj.angle) : undefined }
      }).filter(s => s.text.trim())
    }
  } catch { /* not JSON, try regex splitting */ }

  // Regex fallback: split on numbered patterns like "1.", "**1.", "1)", etc.
  const numbered = cleaned.split(/\n(?=\s*(?:\*{0,2}\d+[\.\)]\s|\*{2}\d+[\.\)]))/g)
  if (numbered.length > 1) {
    return numbered.map(chunk => {
      const text = chunk.replace(/^\s*\*{0,2}\d+[\.\)]\s*\*{0,2}\s*/, '').trim()
      return { text }
    }).filter(s => s.text.length > 20)
  }

  // Last resort: split on double newlines (paragraphs)
  const paragraphs = cleaned.split(/\n\n+/).filter(p => p.trim().length > 20)
  if (paragraphs.length > 1) {
    return paragraphs.map(text => ({ text: text.trim() }))
  }

  return [{ text: cleaned }]
}

const DRAFT_STORAGE_KEY = 'smm_composer_draft'

function saveComposerDraft(data: { contentText: string; selectedConnIds: number[]; attachedMedia: AttachedMedia[]; selectedBoardId: string; scheduledAt: string }) {
  try { sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data)) } catch { /* quota */ }
}

function loadComposerDraft(): { contentText?: string; selectedConnIds?: number[]; attachedMedia?: AttachedMedia[]; selectedBoardId?: string; scheduledAt?: string } | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function clearComposerDraft() {
  try { sessionStorage.removeItem(DRAFT_STORAGE_KEY) } catch { /* noop */ }
}

export default function Composer() {
  const { activeWorkspace } = useWorkspaceContext()
  const wsId = activeWorkspace?.workspaceID ?? 0
  const navigate = useNavigate()
  const location = useLocation()
  const textRef = useRef<HTMLTextAreaElement>(null)
  const [connections, setConnections] = useState<Connection[]>([])
  const [selectedConnIds, setSelectedConnIds] = useState<number[]>([])
  const [contentText, setContentText] = useState('')
  const [attachedMedia, setAttachedMedia] = useState<AttachedMedia[]>([])
  const mediaInputRef = useRef<HTMLInputElement>(null)
  const [showMediaPicker, setShowMediaPicker] = useState(false)
  const [mediaPickerAssets, setMediaPickerAssets] = useState<{ mediaID: number; fileName: string; mimeType: string; blobUrl: string; thumbnailUrl?: string }[]>([])
  const [mediaPickerLoading, setMediaPickerLoading] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')
  const [status, setStatus] = useState<'Draft' | 'Scheduled'>('Draft')
  const [xCostEstimate, setXCostEstimate] = useState<XCostEstimate | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<Post | null>(null)
  const [showEmoji, setShowEmoji] = useState(false)
  const [showAI, setShowAI] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('Polish this post for social media. Improve engagement, fix grammar, suggest relevant hashtags.')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiSuggestions, setAiSuggestions] = useState<{ text: string; angle?: string }[]>([])
  const [aiSelectedIdxs, setAiSelectedIdxs] = useState<Set<number>>(new Set())
  const [aiUseTargets, setAiUseTargets] = useState<string[]>(['__all__'])
  const [aiUseError, setAiUseError] = useState<string | null>(null)
  const [aiTargetOpen, setAiTargetOpen] = useState(false)
  const [draftsSaved, setDraftsSaved] = useState(false)
  const [customContentMap, setCustomContentMap] = useState<Record<string, { enabled: boolean; text: string }>>({})
  const [splitWarning, setSplitWarning] = useState<{ platform: string; limit: number; connectionId: number }[] | null>(null)
  const [pinterestBoards, setPinterestBoards] = useState<{ boardId: string; name: string; pinCount: number }[]>([])
  const [selectedBoardId, setSelectedBoardId] = useState('')
  const [newBoardName, setNewBoardName] = useState('')
  const [creatingBoard, setCreatingBoard] = useState(false)
  const [boardsLoading, setBoardsLoading] = useState(false)
  const [boardError, setBoardError] = useState<string | null>(null)
  const [telegramParseMode, setTelegramParseMode] = useState<'HTML' | 'Markdown' | 'None'>('HTML')
  const [telegramDisablePreview, setTelegramDisablePreview] = useState(false)
  const [publishProgress, setPublishProgress] = useState<string | null>(null)
  const [publishTargetProgress, setPublishTargetProgress] = useState<{ platformName: string; displayName: string; status: string; errorMessage?: string }[]>([])
  const [mediaDetailIdx, setMediaDetailIdx] = useState<number | null>(null)
  const [uploadingToGallery, setUploadingToGallery] = useState(false)
  const [youtubeFormat, setYoutubeFormat] = useState<'long' | 'short'>('long')
  const [youtubeTitle, setYoutubeTitle] = useState('')
  const [youtubeThumbnail, setYoutubeThumbnail] = useState<{ file?: File; preview?: string } | null>(null)
  const ytThumbRef = useRef<HTMLInputElement>(null)
  const [showFormatWarning, setShowFormatWarning] = useState<string | null>(null)
  const [reauthConnections, setReauthConnections] = useState<{ connectionID: number; platformName: string; displayName: string; lastTokenError?: string }[]>([])
  const [showReauthModal, setShowReauthModal] = useState(false)

  useEffect(() => {
    api.get<Connection[]>(`/workspaces/${wsId}/connections`).then(setConnections).catch(err => console.error("[SMM]", err))

    api.get<{ connectionID: number; platformName: string; displayName: string; needsReauth: boolean; lastTokenError?: string }[]>(
      `/workspaces/${wsId}/inbox/connection-health`
    ).then(items => {
      const needs = items.filter(i => i.needsReauth)
      if (needs.length > 0) {
        setReauthConnections(needs)
        setShowReauthModal(true)
      }
    }).catch(() => {})

    const state = location.state as { mediaIds?: number[]; mediaUrls?: string[]; mediaNames?: string[]; mediaMimes?: string[]; draftText?: string } | null
    const saved = loadComposerDraft()

    if (state?.draftText) {
      setContentText(state.draftText)
    } else if (saved?.contentText) {
      setContentText(saved.contentText)
    }

    if (saved?.selectedConnIds?.length) setSelectedConnIds(saved.selectedConnIds)
    if (saved?.selectedBoardId) setSelectedBoardId(saved.selectedBoardId)
    if (saved?.scheduledAt) setScheduledAt(saved.scheduledAt)

    if (state?.mediaUrls && state.mediaUrls.length > 0) {
      const restoredMedia = saved?.attachedMedia ?? []
      const newMedia = state.mediaUrls.map((url, i) => ({
        mediaID: state.mediaIds?.[i] ?? -(Date.now() + i),
        fileName: state.mediaNames?.[i] ?? url.split('/').pop() ?? `file-${i + 1}`,
        blobUrl: url,
        mimeType: state.mediaMimes?.[i] ?? (url.match(/\.(mp4|mov|webm)$/i) ? 'video/mp4' : 'image/jpeg'),
      }))
      const existingUrls = new Set(restoredMedia.map(m => m.blobUrl))
      const deduped = newMedia.filter(m => !existingUrls.has(m.blobUrl))
      setAttachedMedia([...restoredMedia, ...deduped])
    } else if (state?.mediaIds && state.mediaIds.length > 0) {
      const restoredMedia = saved?.attachedMedia ?? []
      Promise.all(state.mediaIds.map(id =>
        api.get<AttachedMedia>(`/workspaces/${wsId}/media/${id}`).catch(() => null)
      )).then(results => {
        const existingIds = new Set(restoredMedia.map(m => m.mediaID))
        const deduped = results.filter((r): r is AttachedMedia => r !== null && !existingIds.has(r.mediaID))
        setAttachedMedia([...restoredMedia, ...deduped])
      })
    } else if (saved?.attachedMedia?.length) {
      setAttachedMedia(saved.attachedMedia)
    }

    if (state) window.history.replaceState({}, '')
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (contentText || attachedMedia.length > 0 || selectedConnIds.length > 0) {
        saveComposerDraft({ contentText, selectedConnIds, attachedMedia, selectedBoardId, scheduledAt })
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [contentText, selectedConnIds, attachedMedia, selectedBoardId, scheduledAt])

  const hasPinterestTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Pinterest'),
    [connections, selectedConnIds])

  const hasTelegramTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Telegram'),
    [connections, selectedConnIds])

  const hasYouTubeTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'YouTube'),
    [connections, selectedConnIds])

  const hasInstagramTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Instagram'),
    [connections, selectedConnIds])
  const [instagramFormat, setInstagramFormat] = useState<'feed' | 'story'>('feed')
  const [instagramHighlightId, setInstagramHighlightId] = useState('')
  const [disableCommentsFor, setDisableCommentsFor] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!hasPinterestTarget) { setPinterestBoards([]); setSelectedBoardId(''); setBoardError(null); setBoardsLoading(false); return }
    const pinterestConn = connections.find(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Pinterest')
    if (!pinterestConn) return
    setBoardsLoading(true)
    setBoardError(null)
    api.get<{ boardId: string; name: string; pinCount: number }[]>(
      `/workspaces/${wsId}/connections/${pinterestConn.connectionID}/pinterest-boards`
    ).then(boards => {
      setPinterestBoards(boards)
      if (boards.length > 0 && !selectedBoardId) setSelectedBoardId(boards[0].boardId)
    }).catch(err => {
      console.error('[SMM] Pinterest boards fetch failed', err)
      const msg = err?.response?.data?.message || err?.message || 'Failed to load Pinterest boards'
      setBoardError(msg)
    }).finally(() => setBoardsLoading(false))
  }, [hasPinterestTarget, selectedConnIds])

  const hasXTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'X/Twitter'),
    [connections, selectedConnIds])

  const hasTikTokTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'TikTok'),
    [connections, selectedConnIds])

  const hasBlueskyTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Bluesky'),
    [connections, selectedConnIds])

  const hasGBPTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Google Business'),
    [connections, selectedConnIds])

  const hasDiscordTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Discord'),
    [connections, selectedConnIds])

  useEffect(() => {
    if (!hasTikTokTarget) { setTiktokCreatorInfo(null); return }
    const tiktokConn = connections.find(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'TikTok')
    if (!tiktokConn) return
    api.get<{
      privacyLevelOptions: string[]
      duetDisabled: boolean
      stitchDisabled: boolean
      commentDisabled: boolean
      maxVideoPostDurationSec: number
      avatarUrl?: string
      username?: string
    }>(`/workspaces/${wsId}/connections/${tiktokConn.connectionID}/tiktok-creator-info`)
      .then(info => {
        setTiktokCreatorInfo(info)
        if (!info.duetDisabled) setTiktokAllowDuet(false)
        if (!info.stitchDisabled) setTiktokAllowStitch(false)
        if (!info.commentDisabled) setTiktokAllowComment(false)
      })
      .catch(err => console.error('[SMM] TikTok creator_info fetch failed', err))
  }, [hasTikTokTarget, connections, selectedConnIds, wsId])

  useEffect(() => {
    if (!hasTikTokTarget) { setTiktokVideoDuration(null); return }
    const vid = attachedMedia.find(m => m.mimeType.startsWith('video'))
    if (!vid) { setTiktokVideoDuration(null); return }
    const el = document.createElement('video')
    el.preload = 'metadata'
    el.src = vid.blobUrl
    el.onloadedmetadata = () => setTiktokVideoDuration(Math.round(el.duration))
    el.onerror = () => setTiktokVideoDuration(null)
    return () => { el.src = '' }
  }, [hasTikTokTarget, attachedMedia])

  const [gbpPostType, setGbpPostType] = useState<'STANDARD' | 'EVENT' | 'OFFER'>('STANDARD')
  const [gbpEventTitle, setGbpEventTitle] = useState('')
  const [gbpEventStartDate, setGbpEventStartDate] = useState('')
  const [gbpEventEndDate, setGbpEventEndDate] = useState('')
  const [gbpOfferCouponCode, setGbpOfferCouponCode] = useState('')
  const [gbpCtaType, setGbpCtaType] = useState('LEARN_MORE')
  const [gbpCtaUrl, setGbpCtaUrl] = useState('')

  const [discordUseEmbed, setDiscordUseEmbed] = useState(false)
  const [discordEmbedTitle, setDiscordEmbedTitle] = useState('')
  const [discordEmbedColor, setDiscordEmbedColor] = useState('#5865F2')
  const [discordEmbedFooter, setDiscordEmbedFooter] = useState('')

  const [tiktokPrivacy, setTiktokPrivacy] = useState<string>('')
  const [tiktokPostMode, setTiktokPostMode] = useState<'post' | 'publish' | ''>('')
  const [tiktokAllowDuet, setTiktokAllowDuet] = useState(false)
  const [tiktokAllowStitch, setTiktokAllowStitch] = useState(false)
  const [tiktokAllowComment, setTiktokAllowComment] = useState(false)
  const [tiktokBrandContent, setTiktokBrandContent] = useState(false)
  const [tiktokBrandOrganic, setTiktokBrandOrganic] = useState(false)
  const [tiktokIsAigc, setTiktokIsAigc] = useState(false)
  const [tiktokDisclosureOn, setTiktokDisclosureOn] = useState(false)
  const [showBrandedBlockModal, setShowBrandedBlockModal] = useState(false)
  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState<{
    privacyLevelOptions: string[]
    duetDisabled: boolean
    stitchDisabled: boolean
    commentDisabled: boolean
    maxVideoPostDurationSec: number
    avatarUrl?: string
    username?: string
  } | null>(null)
  const [tiktokVideoDuration, setTiktokVideoDuration] = useState<number | null>(null)

  const tiktokDurationExceeded = useMemo(() => {
    if (!tiktokCreatorInfo?.maxVideoPostDurationSec || !tiktokVideoDuration) return false
    return tiktokVideoDuration > tiktokCreatorInfo.maxVideoPostDurationSec
  }, [tiktokCreatorInfo, tiktokVideoDuration])

  const [xAutoThread, setXAutoThread] = useState(true)
  const [blueskyAutoThread, setBlueskyAutoThread] = useState(true)
  const [threadsAutoThread, setThreadsAutoThread] = useState(true)
  const [previewOpen, setPreviewOpen] = useState(() => window.innerWidth > 900)

  const hasThreadsTarget = useMemo(() =>
    connections.some(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Threads'),
    [connections, selectedConnIds])
  const [activeSettingsTab, setActiveSettingsTab] = useState<string>('')

  const settingsTabs = useMemo(() => {
    const tabs: string[] = []
    const selectedPlatforms = new Set(
      connections.filter(c => selectedConnIds.includes(c.connectionID) && c.platformName).map(c => c.platformName!)
    )
    if (hasInstagramTarget) tabs.push('Instagram')
    if (hasYouTubeTarget) tabs.push('YouTube')
    if (hasPinterestTarget) tabs.push('Pinterest')
    if (hasTelegramTarget) tabs.push('Telegram')
    if (hasTikTokTarget) tabs.push('TikTok')
    if (hasXTarget) tabs.push('X/Twitter')
    if (hasBlueskyTarget) tabs.push('Bluesky')
    if (hasGBPTarget) tabs.push('Google Business')
    if (hasDiscordTarget) tabs.push('Discord')
    if (hasThreadsTarget) tabs.push('Threads')
    selectedPlatforms.forEach(p => { if (!tabs.includes(p)) tabs.push(p) })
    return tabs
  }, [hasInstagramTarget, hasYouTubeTarget, hasPinterestTarget, hasTelegramTarget, hasTikTokTarget, hasXTarget, hasBlueskyTarget, hasGBPTarget, hasDiscordTarget, hasThreadsTarget, connections, selectedConnIds])

  useEffect(() => {
    if (settingsTabs.length > 0 && !settingsTabs.includes(activeSettingsTab)) {
      setActiveSettingsTab(settingsTabs[0])
    } else if (settingsTabs.length === 0) {
      setActiveSettingsTab('')
    }
  }, [settingsTabs])

  const xEffectiveText = useMemo(() => {
    const cc = customContentMap['X/Twitter']
    return (cc?.enabled && cc.text) ? cc.text : contentText
  }, [contentText, customContentMap])
  const hasUrl = useMemo(() => /https?:\/\//.test(xEffectiveText), [xEffectiveText])

  useEffect(() => {
    if (!hasXTarget || !xEffectiveText) { setXCostEstimate(null); return }
    const timeout = setTimeout(() => {
      api.post<XCostEstimate>(`/workspaces/${wsId}/posts/x-cost-estimate`, { contentText: xEffectiveText, hasUrl }).then(setXCostEstimate).catch(err => console.error("[SMM]", err))
    }, 500)
    return () => clearTimeout(timeout)
  }, [xEffectiveText, hasXTarget, hasUrl])

  const toggleConnection = (id: number) => setSelectedConnIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const wrapSelection = useCallback((before: string, after: string) => {
    const ta = textRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = contentText.substring(start, end)
    const newText = contentText.substring(0, start) + before + selected + after + contentText.substring(end)
    setContentText(newText)
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + before.length, end + before.length) }, 0)
  }, [contentText])

  const insertEmoji = (emoji: string) => {
    const ta = textRef.current
    if (!ta) return
    const pos = ta.selectionStart
    setContentText(contentText.substring(0, pos) + emoji + contentText.substring(pos))
    setShowEmoji(false)
    setTimeout(() => { ta.focus(); ta.setSelectionRange(pos + emoji.length, pos + emoji.length) }, 0)
  }

  const checkImageLimits = (): { platform: string; limit: number; connectionId: number }[] => {
    const imageCount = attachedMedia.filter(m => m.mimeType.startsWith('image')).length
    if (imageCount <= 1) return []
    return selectedConnIds.map(id => {
      const conn = connections.find(c => c.connectionID === id)
      if (!conn?.platformName) return null
      const limit = PLATFORM_IMAGE_LIMITS[conn.platformName] || 10
      if (imageCount > limit) return { platform: conn.platformName, limit, connectionId: id }
      return null
    }).filter(Boolean) as { platform: string; limit: number; connectionId: number }[]
  }

  const renderCustomContentBlock = (platform: string) => {
    const cc = customContentMap[platform] ?? { enabled: false, text: '' }
    const limit = PLATFORM_CHAR_LIMITS[platform]
    const isAutoThreadPlatform = platform === 'X/Twitter' || platform === 'Bluesky' || platform === 'Threads'
    const autoThreadEnabled = platform === 'X/Twitter' ? xAutoThread : platform === 'Bluesky' ? blueskyAutoThread : platform === 'Threads' ? threadsAutoThread : false
    return (
      <div style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #f0f0f0' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={cc.enabled}
            onChange={e => {
              const next = e.target.checked
              setCustomContentMap(prev => ({ ...prev, [platform]: { ...prev[platform], enabled: next, text: prev[platform]?.text ?? '' } }))
              if (next && isAutoThreadPlatform) {
                if (platform === 'X/Twitter') setXAutoThread(false)
                if (platform === 'Bluesky') setBlueskyAutoThread(false)
                if (platform === 'Threads') setThreadsAutoThread(false)
              }
            }} />
          Custom Content
        </label>
        {isAutoThreadPlatform && cc.enabled && autoThreadEnabled && (
          <div style={{ fontSize: '0.72rem', color: '#b45309', padding: '0.2rem 0' }}>
            Auto-threading has been disabled because custom content is active.
          </div>
        )}
        {cc.enabled && (
          <>
            <textarea className="smm-input" placeholder={`Custom content for ${platform}...`}
              value={cc.text}
              onChange={e => setCustomContentMap(prev => ({ ...prev, [platform]: { ...prev[platform], enabled: true, text: e.target.value } }))}
              style={{ width: '100%', minHeight: 80, padding: '0.4rem 0.5rem', fontSize: '0.85rem', marginTop: '0.35rem', resize: 'vertical' }} />
            {limit && (
              <div style={{ fontSize: '0.72rem', textAlign: 'right', color: cc.text.length > limit ? '#ef4444' : '#9ca3af', marginTop: '0.15rem' }}>
                {cc.text.length}/{limit}
              </div>
            )}
            <div className="smm-hint" style={{ fontSize: '0.72rem' }}>
              This text will be sent to {platform} instead of the main content above.
              {isAutoThreadPlatform ? ' Auto-threading is disabled when custom content is active.' : ''}
            </div>
          </>
        )}
      </div>
    )
  }

  const doSubmit = async (splitMode: 'none' | 'split-drafts' | 'truncate' = 'none') => {
    if (!contentText.trim()) { setError('Content is required'); return }
    if (selectedConnIds.length === 0) { setError('Select at least one target platform'); return }
    if (hasInstagramTarget && attachedMedia.length === 0) { setError('Instagram requires at least one image or video -- text-only posts are not supported'); return }
    if (hasPinterestTarget && !attachedMedia.some(m => m.mimeType.startsWith('image'))) { setError('Pinterest requires at least one image'); return }
    if (hasTikTokTarget && !attachedMedia.some(m => m.mimeType.startsWith('video'))) { setError('TikTok requires a video'); return }
    if (hasTikTokTarget && !tiktokPostMode) { setError('Select a TikTok Post Mode (Upload or Publish) before posting'); return }
    setSubmitting(true); setError(null); setSplitWarning(null)
    try {
      const finalStatus = scheduledAt ? 'Scheduled' : status
      const images = attachedMedia.filter(m => m.mimeType.startsWith('image'))

      if (splitMode === 'split-drafts' && images.length > 1) {
        const minLimit = Math.min(...selectedConnIds.map(id => {
          const conn = connections.find(c => c.connectionID === id)
          return PLATFORM_IMAGE_LIMITS[conn?.platformName || ''] || 10
        }))
        const firstBatch = images.slice(0, minLimit)
        const overflow = images.slice(minLimit)

        const mediaJson = JSON.stringify(firstBatch.map(m => ({ mediaID: m.mediaID, url: m.blobUrl, fileName: m.fileName, mimeType: m.mimeType })))
        const postId = await api.post<number>(`/workspaces/${wsId}/posts`, { contentText, scheduledAt: scheduledAt || null, status: finalStatus, targetConnectionIds: selectedConnIds, mediaJson })
        clearComposerDraft()

        for (let i = 0; i < overflow.length; i += minLimit) {
          const chunk = overflow.slice(i, i + minLimit)
          await api.post(`/workspaces/${wsId}/posts/drafts`, {
            contentText: `(continued) ${contentText.slice(0, 100)}...`,
            mediaJson: JSON.stringify(chunk.map(m => ({ mediaID: m.mediaID, url: m.blobUrl, fileName: m.fileName, mimeType: m.mimeType }))),
            source: 'manual',
            targetPlatforms: connections.filter(c => selectedConnIds.includes(c.connectionID)).map(c => c.platformName).join(','),
          })
        }
        const post = await api.get<Post>(`/workspaces/${wsId}/posts/${postId}`)
        setSuccess(post)
      } else {
        let finalMedia = attachedMedia
        if (splitMode === 'truncate') {
          const minLimit = Math.min(...selectedConnIds.map(id => {
            const conn = connections.find(c => c.connectionID === id)
            return PLATFORM_IMAGE_LIMITS[conn?.platformName || ''] || 10
          }))
          finalMedia = images.slice(0, minLimit)
        }
        const mediaItems2: Record<string, unknown>[] = finalMedia.map(m => ({ mediaID: m.mediaID, url: m.blobUrl, fileName: m.fileName, mimeType: m.mimeType }))
        if (hasYouTubeTarget) {
          const ytM: Record<string, string> = { youtubeFormat }
          if (youtubeTitle.trim()) ytM.youtubeTitle = youtubeTitle.trim()
          mediaItems2.push({ _platformMeta: ytM })
        }
        if (hasTikTokTarget) {
          const ttM: Record<string, string> = { tiktokPrivacyLevel: tiktokPrivacy || 'SELF_ONLY', tiktokPostMode: tiktokPostMode || 'publish' }
          if (!tiktokAllowDuet) ttM.tiktokDisableDuet = 'true'
          if (!tiktokAllowStitch) ttM.tiktokDisableStitch = 'true'
          if (!tiktokAllowComment) ttM.tiktokDisableComment = 'true'
          if (tiktokBrandContent) ttM.tiktokBrandContentToggle = 'true'
          if (tiktokBrandOrganic) ttM.tiktokBrandOrganicToggle = 'true'
          if (tiktokIsAigc) ttM.tiktokIsAigc = 'true'
          ttM.tiktokMusicUsageConfirmation = 'true'
          mediaItems2.push({ _platformMeta: ttM })
        }
        if (hasPinterestTarget && selectedBoardId) {
          mediaItems2.push({ _platformMeta: { pinterestBoardId: selectedBoardId } })
        }
        if (hasXTarget && xAutoThread) {
          mediaItems2.push({ _platformMeta: { xAutoThread: 'true' } })
        }
        if (hasBlueskyTarget && blueskyAutoThread) {
          mediaItems2.push({ _platformMeta: { blueskyAutoThread: 'true' } })
        }
        if (hasThreadsTarget && threadsAutoThread) {
          mediaItems2.push({ _platformMeta: { threadsAutoThread: 'true' } })
        }
        if (hasGBPTarget) {
          const gbpM: Record<string, string> = { gbpPostType, gbpCtaType }
          if (gbpCtaUrl.trim()) gbpM.gbpCtaUrl = gbpCtaUrl.trim()
          if (gbpPostType === 'EVENT') {
            if (gbpEventTitle.trim()) gbpM.gbpEventTitle = gbpEventTitle.trim()
            if (gbpEventStartDate) gbpM.gbpEventStartDate = gbpEventStartDate
            if (gbpEventEndDate) gbpM.gbpEventEndDate = gbpEventEndDate
          }
          if (gbpPostType === 'OFFER') {
            if (gbpOfferCouponCode.trim()) gbpM.gbpOfferCouponCode = gbpOfferCouponCode.trim()
            if (gbpEventStartDate) gbpM.gbpEventStartDate = gbpEventStartDate
            if (gbpEventEndDate) gbpM.gbpEventEndDate = gbpEventEndDate
          }
          mediaItems2.push({ _platformMeta: gbpM })
        }
        if (hasDiscordTarget) {
          const dM: Record<string, string> = {}
          if (discordUseEmbed) {
            dM.discordUseEmbed = 'true'
            if (discordEmbedTitle.trim()) dM.discordEmbedTitle = discordEmbedTitle.trim()
            if (discordEmbedColor) dM.discordEmbedColor = parseInt(discordEmbedColor.replace('#', ''), 16).toString()
            if (discordEmbedFooter.trim()) dM.discordEmbedFooter = discordEmbedFooter.trim()
          }
          mediaItems2.push({ _platformMeta: dM })
        }
        if (disableCommentsFor.size > 0) {
          const dcM: Record<string, string> = {}
          disableCommentsFor.forEach(p => { dcM[`disableComments_${p.toLowerCase()}`] = 'true' })
          mediaItems2.push({ _platformMeta: dcM })
        }
        const ccM: Record<string, string> = {}
        Object.entries(customContentMap).forEach(([platform, { enabled, text }]) => {
          if (enabled && text.trim()) ccM[`customcontent_${platform.toLowerCase()}`] = text.trim()
        })
        if (Object.keys(ccM).length > 0) mediaItems2.push({ _platformMeta: ccM })
        const mediaJson = mediaItems2.length > 0 ? JSON.stringify(mediaItems2) : null
        const postId = await api.post<number>(`/workspaces/${wsId}/posts`, { contentText, scheduledAt: scheduledAt || null, status: finalStatus, targetConnectionIds: selectedConnIds, mediaJson })
        const post = await api.get<Post>(`/workspaces/${wsId}/posts/${postId}`)
        clearComposerDraft()
        setSuccess(post)
      }
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed to create post') }
    finally { setSubmitting(false) }
  }

  const handleSubmit = async () => {
    const overLimits = checkImageLimits()
    if (overLimits.length > 0) {
      setSplitWarning(overLimits)
      return
    }
    doSubmit('none')
  }

  const handlePublishNow = async () => {
    if (!contentText.trim()) { setError('Content is required'); return }
    if (selectedConnIds.length === 0) { setError('Select at least one target platform'); return }
    if (hasInstagramTarget && attachedMedia.length === 0) { setError('Instagram requires at least one image or video -- text-only posts are not supported'); return }
    if (hasPinterestTarget && !attachedMedia.some(m => m.mimeType.startsWith('image'))) { setError('Pinterest requires at least one image'); return }
    if (hasTikTokTarget && !attachedMedia.some(m => m.mimeType.startsWith('video'))) { setError('TikTok requires a video'); return }
    if (hasTikTokTarget && !tiktokPostMode) { setError('Select a TikTok Post Mode (Upload or Publish) before posting'); return }
    if (hasTikTokTarget && tiktokPostMode === 'publish' && !tiktokPrivacy) { setError('Select a TikTok privacy level before publishing'); return }
    if (hasTikTokTarget && tiktokDurationExceeded) { setError(`Video is ${tiktokVideoDuration}s -- this TikTok account's limit is ${tiktokCreatorInfo?.maxVideoPostDurationSec}s. Trim it or choose a shorter clip.`); return }

    if (hasYouTubeTarget && attachedMedia.some(m => m.mimeType.startsWith('video'))) {
      const vid = attachedMedia.find(m => m.mimeType.startsWith('video'))
      if (vid) {
        const warning = await new Promise<string | null>(resolve => {
          const videoEl = document.createElement('video')
          videoEl.preload = 'metadata'
          videoEl.src = vid.blobUrl
          videoEl.onloadedmetadata = () => {
            const isVertical = videoEl.videoHeight > videoEl.videoWidth
            const isUnder60 = videoEl.duration <= 60
            const selectedShort = youtubeFormat === 'short'
            if (selectedShort && !isVertical) {
              resolve('You selected YouTube Shorts but this video appears to be landscape (horizontal). Shorts are typically vertical (9:16). Continue anyway?')
            } else if (selectedShort && !isUnder60) {
              resolve(`You selected YouTube Shorts but this video is ${Math.round(videoEl.duration)}s long. Shorts must be 60 seconds or less. Continue anyway?`)
            } else if (!selectedShort && isVertical && isUnder60) {
              resolve('This video is vertical and under 60 seconds -- it looks like a Short. You selected "Video (Long)". Continue anyway?')
            } else {
              resolve(null)
            }
          }
          videoEl.onerror = () => resolve(null)
        })
        if (warning) {
          setShowFormatWarning(warning)
          return
        }
      }
    }

    setSubmitting(true); setError(null); setPublishProgress('Preparing post...'); setPublishTargetProgress([])
    try {
      const mediaItems: Record<string, unknown>[] = attachedMedia.map(m => ({ mediaID: m.mediaID, url: m.blobUrl, fileName: m.fileName, mimeType: m.mimeType }))

      if (youtubeThumbnail?.preview) {
        mediaItems.push({ mediaID: 0, url: youtubeThumbnail.preview, fileName: 'thumbnail.png', mimeType: 'image/png', _isYouTubeThumbnail: true })
      }

      if (hasYouTubeTarget) {
        const ytMeta: Record<string, string> = { youtubeFormat }
        if (youtubeTitle.trim()) ytMeta.youtubeTitle = youtubeTitle.trim()
        if (youtubeThumbnail?.preview) ytMeta.youtubeThumbnail = '__resolve_thumbnail__'
        mediaItems.push({ _platformMeta: ytMeta })
      }

      if (hasInstagramTarget && instagramFormat !== 'feed') {
        const igMeta: Record<string, string> = { instagramFormat }
        if (instagramHighlightId.trim()) igMeta.instagramHighlightId = instagramHighlightId.trim()
        mediaItems.push({ _platformMeta: { ...igMeta } })
      }

      if (disableCommentsFor.size > 0) {
        const dcMeta: Record<string, string> = {}
        disableCommentsFor.forEach(p => { dcMeta[`disableComments_${p.toLowerCase()}`] = 'true' })
        mediaItems.push({ _platformMeta: dcMeta })
      }

      if (hasTikTokTarget) {
        const ttMeta: Record<string, string> = { tiktokPrivacyLevel: tiktokPrivacy || 'SELF_ONLY', tiktokPostMode: tiktokPostMode || 'publish' }
        if (!tiktokAllowDuet) ttMeta.tiktokDisableDuet = 'true'
        if (!tiktokAllowStitch) ttMeta.tiktokDisableStitch = 'true'
        if (!tiktokAllowComment) ttMeta.tiktokDisableComment = 'true'
        if (tiktokBrandContent) ttMeta.tiktokBrandContentToggle = 'true'
        if (tiktokBrandOrganic) ttMeta.tiktokBrandOrganicToggle = 'true'
        if (tiktokIsAigc) ttMeta.tiktokIsAigc = 'true'
        ttMeta.tiktokMusicUsageConfirmation = 'true'
        mediaItems.push({ _platformMeta: ttMeta })
      }

      if (hasXTarget && xAutoThread) {
        mediaItems.push({ _platformMeta: { xAutoThread: 'true' } })
      }

      if (hasBlueskyTarget && blueskyAutoThread) {
        mediaItems.push({ _platformMeta: { blueskyAutoThread: 'true' } })
      }

      if (hasThreadsTarget && threadsAutoThread) {
        mediaItems.push({ _platformMeta: { threadsAutoThread: 'true' } })
      }

      if (hasGBPTarget) {
        const gbpMeta: Record<string, string> = { gbpPostType, gbpCtaType }
        if (gbpCtaUrl.trim()) gbpMeta.gbpCtaUrl = gbpCtaUrl.trim()
        if (gbpPostType === 'EVENT') {
          if (gbpEventTitle.trim()) gbpMeta.gbpEventTitle = gbpEventTitle.trim()
          if (gbpEventStartDate) gbpMeta.gbpEventStartDate = gbpEventStartDate
          if (gbpEventEndDate) gbpMeta.gbpEventEndDate = gbpEventEndDate
        }
        if (gbpPostType === 'OFFER') {
          if (gbpOfferCouponCode.trim()) gbpMeta.gbpOfferCouponCode = gbpOfferCouponCode.trim()
          if (gbpEventStartDate) gbpMeta.gbpEventStartDate = gbpEventStartDate
          if (gbpEventEndDate) gbpMeta.gbpEventEndDate = gbpEventEndDate
        }
        mediaItems.push({ _platformMeta: gbpMeta })
      }

      if (hasDiscordTarget) {
        const dMeta: Record<string, string> = {}
        if (discordUseEmbed) {
          dMeta.discordUseEmbed = 'true'
          if (discordEmbedTitle.trim()) dMeta.discordEmbedTitle = discordEmbedTitle.trim()
          if (discordEmbedColor) dMeta.discordEmbedColor = parseInt(discordEmbedColor.replace('#', ''), 16).toString()
          if (discordEmbedFooter.trim()) dMeta.discordEmbedFooter = discordEmbedFooter.trim()
        }
        mediaItems.push({ _platformMeta: dMeta })
      }

      const ccMeta: Record<string, string> = {}
      Object.entries(customContentMap).forEach(([platform, { enabled, text }]) => {
        if (enabled && text.trim()) ccMeta[`customcontent_${platform.toLowerCase()}`] = text.trim()
      })
      if (Object.keys(ccMeta).length > 0) mediaItems.push({ _platformMeta: ccMeta })

      const mediaJson2 = mediaItems.length > 0 ? JSON.stringify(mediaItems) : null

      if (attachedMedia.length > 0) setPublishProgress('Uploading media...')
      const postId = await api.post<number>(`/workspaces/${wsId}/posts`, { contentText, status: 'Draft', targetConnectionIds: selectedConnIds, mediaJson: mediaJson2 })

      const initialTargets = selectedConnIds.map(id => {
        const conn = connections.find(c => c.connectionID === id)
        return { platformName: conn?.platformName || '?', displayName: conn?.displayName || '', status: 'Pending' }
      })
      setPublishTargetProgress(initialTargets)
      setPublishProgress(`Publishing to ${selectedConnIds.length} platform${selectedConnIds.length > 1 ? 's' : ''}...`)

      const publishPromise = api.post(`/workspaces/${wsId}/posts/${postId}/publish`)

      const pollInterval = setInterval(async () => {
        try {
          const updated = await api.get<Post>(`/workspaces/${wsId}/posts/${postId}`)
          if (updated.targets?.length) {
            setPublishTargetProgress(updated.targets.map(t => ({
              platformName: t.platformName || '?',
              displayName: t.displayName || '',
              status: t.status,
              errorMessage: t.errorMessage || undefined,
            })))
            const done = updated.targets.every(t => t.status !== 'Pending')
            const completed = updated.targets.filter(t => t.status !== 'Pending').length
            setPublishProgress(`Published ${completed}/${updated.targets.length} platforms...`)
            if (done) clearInterval(pollInterval)
          }
        } catch { /* polling failure is non-fatal */ }
      }, 2000)

      await publishPromise
      clearInterval(pollInterval)

      setPublishProgress('Finalising...')
      const post = await api.get<Post>(`/workspaces/${wsId}/posts/${postId}`)
      clearComposerDraft()
      setSuccess(post)
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed to publish') }
    finally { setSubmitting(false); setPublishProgress(null); setPublishTargetProgress([]) }
  }

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) return
    const pinterestConn = connections.find(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'Pinterest')
    if (!pinterestConn) return
    setCreatingBoard(true)
    try {
      const board = await api.post<{ boardId: string; name: string }>(`/workspaces/${wsId}/connections/${pinterestConn.connectionID}/pinterest-boards`, { name: newBoardName.trim() })
      setPinterestBoards(prev => [...prev, { boardId: board.boardId, name: board.name, pinCount: 0 }])
      setSelectedBoardId(board.boardId)
      setNewBoardName('')
    } catch (err: unknown) {
      console.error('[SMM] Create board failed', err)
      const apiMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(apiMsg || 'Failed to create Pinterest board')
    }
    finally { setCreatingBoard(false) }
  }

  if (splitWarning && splitWarning.length > 0) {
    const imageCount = attachedMedia.filter(m => m.mimeType.startsWith('image')).length
    return (
      <div className="smm-composer">
        <div className="smm-card" style={{ border: '2px solid #f59e0b', padding: '1.5rem' }}>
          <h2 style={{ marginTop: 0 }}>Image Limit Exceeded</h2>
          <p>You have <strong>{imageCount} images</strong> attached, but some platforms have lower limits:</p>
          <ul style={{ margin: '0.5rem 0' }}>
            {splitWarning.map(w => <li key={w.connectionId}><strong>{w.platform}</strong>: max {w.limit} images</li>)}
          </ul>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button className="smm-btn smm-btn-primary" onClick={() => doSubmit('split-drafts')}>
              Split into multiple posts (overflow saved as drafts)
            </button>
            <button className="smm-btn" onClick={() => doSubmit('truncate')}>
              Post with first {Math.min(...splitWarning.map(w => w.limit))} images only
            </button>
            <button className="smm-btn" onClick={() => doSubmit('none')}>
              Post anyway (platform may reject)
            </button>
            <button className="smm-btn smm-btn-sm" onClick={() => setSplitWarning(null)}>Cancel</button>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="smm-composer">
        <div className="smm-card smm-success-card">
          <h2>Post Created</h2>
          <p>Status: <strong>{success.status}</strong></p>
          {success.targets?.map(t => {
            const url = t.status === 'Published' ? getPlatformPostUrl(t.platformName, t.platformPostID, t.displayName) : null
            return (
              <div key={t.postTargetID} className="smm-target-result">
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 500 }}>
                    {t.platformName} ({t.displayName}) &#x2197;
                  </a>
                ) : (
                  <span>{t.platformName} ({t.displayName})</span>
                )}
                <span className={`smm-badge smm-badge-${t.status.toLowerCase()}`}>{t.status}</span>
                {t.errorMessage && t.status !== 'Published' && <span className="smm-error-text">{t.errorMessage}</span>}
                {t.errorMessage && t.status === 'Published' && <span style={{ color: '#1e40af', fontSize: '0.78rem', display: 'block', marginTop: '0.2rem' }}>{t.errorMessage}</span>}
              </div>
            )
          })}
          <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.8rem', background: '#eef6ff', border: '1px solid #93c5fd', borderRadius: 6, fontSize: '0.82rem', lineHeight: 1.5 }}>
            Content posted -- this can take a few minutes to finish processing and appear on each platform.
          </div>
          {tiktokPostMode === 'post' && success.targets?.some(t => t.platformName === 'TikTok') && (
            <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.8rem', background: '#fef3cd', border: '1px solid #ffc107', borderRadius: 6, fontSize: '0.82rem', lineHeight: 1.5 }}>
              <strong>TikTok Upload:</strong> Sent to your TikTok inbox -- open the TikTok app and tap the notification to finish editing and post.
            </div>
          )}
          <div className="smm-actions">
            <button onClick={() => { setSuccess(null); setContentText(''); setSelectedConnIds([]); setAttachedMedia([]); setDisableCommentsFor(new Set()); setTiktokPrivacy(''); setTiktokPostMode(''); setTiktokAllowDuet(false); setTiktokAllowStitch(false); setTiktokAllowComment(false); setTiktokBrandContent(false); setTiktokBrandOrganic(false); setXAutoThread(true); setBlueskyAutoThread(true) }} className="smm-btn smm-btn-primary">New Post</button>
            <button onClick={() => navigate('/posts')} className="smm-btn">View All Posts</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="smm-composer">
      <h1>Create Post</h1>
      {showBrandedBlockModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.45)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowBrandedBlockModal(false)}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '24px 28px', maxWidth: 460, width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#b45309' }}>
              Branded Content Unavailable in Publish Mode
            </h3>
            <p style={{ margin: '0 0 12px', fontSize: '0.88rem', color: '#555', lineHeight: 1.6 }}>
              While this app is awaiting TikTok compliance review, all published videos must use <strong>Private (Only Me)</strong> privacy.
              Branded Content requires non-private privacy and cannot be combined with the Private restriction.
            </p>
            <p style={{ margin: '0 0 16px', fontSize: '0.88rem', color: '#555', lineHeight: 1.6 }}>
              To use Branded Content now, switch to <strong>Upload</strong> mode — the video will be sent to your TikTok inbox where you can set privacy and branded content labels before posting.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="smm-btn" onClick={() => setShowBrandedBlockModal(false)}>Cancel</button>
              <button className="smm-btn smm-btn-primary" onClick={() => {
                setShowBrandedBlockModal(false)
                setTiktokPostMode('post')
                setTiktokBrandContent(true)
              }}>Switch to Upload Mode</button>
            </div>
          </div>
        </div>
      )}
      {showReauthModal && reauthConnections.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.45)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowReauthModal(false)}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '24px 28px', maxWidth: 480, width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#d32f2f' }}>
              Reconnect Required
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.88rem', color: '#555', lineHeight: 1.5 }}>
              The following connections have expired or encountered an error. Publishing to these accounts will fail until reconnected.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {reauthConnections.map(c => (
                <div key={c.connectionID} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', borderRadius: 8, background: '#fff3f3', border: '1px solid #ffcdd2'
                }}>
                  <div>
                    <strong style={{ fontSize: '0.88rem' }}>{c.platformName}</strong>
                    <span style={{ fontSize: '0.8rem', color: '#777', marginLeft: 6 }}>{c.displayName}</span>
                    {c.lastTokenError && (
                      <div style={{ fontSize: '0.75rem', color: '#b71c1c', marginTop: 2 }}>{c.lastTokenError}</div>
                    )}
                  </div>
                  <button
                    onClick={() => { setShowReauthModal(false); navigate(`/connections?reconnect=${encodeURIComponent(c.platformName)}`) }}
                    style={{
                      padding: '5px 14px', borderRadius: 6, border: 'none', fontSize: '0.82rem',
                      background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap'
                    }}>Reconnect</button>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'right' }}>
              <button onClick={() => setShowReauthModal(false)} style={{
                padding: '6px 18px', borderRadius: 6, border: '1px solid #ccc',
                background: '#f5f5f5', cursor: 'pointer', fontSize: '0.82rem'
              }}>Dismiss</button>
            </div>
          </div>
        </div>
      )}
      <div className="smm-composer-grid">
        <div className="smm-composer-editor">
          <div className="smm-card">
            <h3>Content</h3>
            <div className="smm-rte-toolbar">
              <button type="button" title="Bold" onClick={() => wrapSelection('**', '**')} className="smm-rte-btn"><strong>B</strong></button>
              <button type="button" title="Italic" onClick={() => wrapSelection('_', '_')} className="smm-rte-btn"><em>I</em></button>
              <button type="button" title="Strikethrough" onClick={() => wrapSelection('~~', '~~')} className="smm-rte-btn"><s>S</s></button>
              <span className="smm-rte-sep" />
              <button type="button" title="Emoji" onClick={() => setShowEmoji(!showEmoji)} className="smm-rte-btn">😀</button>
            </div>
            {showEmoji && (
              <div className="smm-emoji-picker">
                {EMOJI_PICKS.map(e => <button key={e} type="button" className="smm-emoji-btn" onClick={() => insertEmoji(e)}>{e}</button>)}
              </div>
            )}
            <textarea ref={textRef} className="smm-textarea" rows={6} value={contentText} onChange={e => setContentText(e.target.value)} placeholder="What would you like to share?" />

            <div style={{ borderTop: '1px solid #eee', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
              <button type="button" className="smm-btn smm-btn-sm" onClick={() => setShowAI(!showAI)}
                style={{ background: showAI ? 'var(--smm-primary, #2563eb)' : undefined,
                  color: showAI ? '#fff' : undefined }}>
                AI Assist {showAI ? '\u25B2' : '\u25BC'}
              </button>
              {showAI && (
                <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                  <AIPromptInput
                    mode="text"
                    prompt={aiPrompt}
                    onPromptChange={setAiPrompt}
                    generating={aiLoading}
                    promptPlaceholder="Describe what you want: e.g. 'Write 3 promotional posts for our summer festival' or 'Refine this text for Instagram'"
                    generateLabel={aiLoading ? 'Generating...' : 'Generate'}
                    onGenerate={async (values: AIPromptInputValues) => {
                      setAiLoading(true); setAiSuggestions([]); setAiSelectedIdxs(new Set()); setDraftsSaved(false)
                      try {
                        const platforms = connections.filter(c => selectedConnIds.includes(c.connectionID)).map(c => c.platformName)
                        const res = await api.post<{ suggestions: string; model?: string }>(`/workspaces/${wsId}/posts/ai-assist`, {
                          prompt: values.prompt || 'Suggest improvements for this post',
                          mode: contentText.trim() ? 'polish' : 'suggest',
                          platform: platforms[0] || 'general',
                          text: contentText || undefined,
                          tier: values.tier,
                          reasoning: values.reasoning,
                        })
                        const items = parseSuggestions(res.suggestions)
                        setAiSuggestions(items)
                      } catch (err) {
                        console.error('[SMM] AI assist failed', err)
                        setAiSuggestions([{ text: 'AI suggestion failed. Check that an AI provider is configured in Admin Settings.' }])
                      } finally { setAiLoading(false) }
                    }}
                  />
                  {aiUseError && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => setAiUseError(null)}>
                      <div style={{ background: '#fff', borderRadius: 10, padding: '1.5rem 2rem', maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                        onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 0.75rem' }}>Cannot Use Multiple Suggestions</h3>
                        <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#555' }}>{aiUseError}</p>
                        <button className="smm-btn smm-btn-primary" onClick={() => setAiUseError(null)}>OK</button>
                      </div>
                    </div>
                  )}
                  {aiSuggestions.length > 0 && (
                    <div style={{ marginTop: '0.5rem', borderTop: '1px solid #eee', paddingTop: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: '0.85rem' }}>Suggestions</strong>
                        <button className="smm-btn smm-btn-sm"
                          onClick={() => setAiSelectedIdxs(prev =>
                            prev.size === aiSuggestions.length ? new Set() : new Set(aiSuggestions.map((_, i) => i))
                          )}>
                          {aiSelectedIdxs.size === aiSuggestions.length ? 'Uncheck All' : 'Check All'}
                        </button>
                        {(() => {
                          const targetPlatforms = connections
                            .filter(c => selectedConnIds.includes(c.connectionID) && c.platformName)
                            .reduce<{ value: string; label: string }[]>((acc, c) => {
                              if (!acc.some(x => x.value === c.platformName)) acc.push({ value: c.platformName!, label: c.platformName! })
                              return acc
                            }, [])
                          const isMainContent = aiUseTargets.includes('__all__') || aiUseTargets.length === 0
                          const targetLabel = isMainContent
                            ? 'All (Main Content)'
                            : aiUseTargets.length === 1
                              ? aiUseTargets[0]
                              : `${aiUseTargets.length} platforms`
                          return (
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <button type="button" className="smm-btn smm-btn-sm"
                                onClick={() => setAiTargetOpen(!aiTargetOpen)}
                                style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {targetLabel} <span style={{ fontSize: '0.65rem', transform: aiTargetOpen ? 'rotate(180deg)' : 'none' }}>&#9660;</span>
                              </button>
                              {aiTargetOpen && (
                                <div style={{
                                  position: 'absolute', top: '100%', left: 0, zIndex: 50, minWidth: 200,
                                  background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '4px 0'
                                }}>
                                  <label style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                                    cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                                    background: isMainContent ? '#f0f5ff' : 'transparent'
                                  }}
                                    onClick={() => { setAiUseTargets(['__all__']); setAiTargetOpen(false) }}>
                                    <input type="radio" checked={isMainContent} readOnly
                                      style={{ accentColor: 'var(--smm-primary, #4361ee)' }} />
                                    All (Main Content)
                                  </label>
                                  <div style={{ borderTop: '1px solid #e5e7eb', margin: '2px 0' }} />
                                  {targetPlatforms.map(p => {
                                    const checked = aiUseTargets.includes(p.value) && !isMainContent
                                    return (
                                      <label key={p.value} style={{
                                        display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px',
                                        cursor: 'pointer', fontSize: '0.85rem',
                                        background: checked ? '#f0f5ff' : 'transparent'
                                      }}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          if (isMainContent) {
                                            setAiUseTargets([p.value])
                                          } else {
                                            setAiUseTargets(prev =>
                                              prev.includes(p.value) ? prev.filter(v => v !== p.value) : [...prev.filter(v => v !== '__all__'), p.value]
                                            )
                                          }
                                        }}>
                                        <input type="checkbox" checked={checked} readOnly
                                          style={{ accentColor: 'var(--smm-primary, #4361ee)' }} />
                                        {p.label}
                                      </label>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })()}
                        <button className="smm-btn smm-btn-sm smm-btn-primary"
                          disabled={aiSelectedIdxs.size === 0}
                          onClick={() => {
                            const selected = Array.from(aiSelectedIdxs).map(i => aiSuggestions[i])
                            if (selected.length > 1) {
                              setAiUseError('Only 1 suggestion can be used at a time. Select a single suggestion to use, or save multiple to drafts.')
                              return
                            }
                            if (selected.length === 0) return
                            const text = selected[0].text
                            if (aiUseTargets.includes('__all__') || aiUseTargets.length === 0) {
                              setContentText(text)
                            } else {
                              aiUseTargets.forEach(platform => {
                                if (platform !== '__all__') {
                                  setCustomContentMap(prev => ({ ...prev, [platform]: { enabled: true, text } }))
                                  if (platform === 'X/Twitter') setXAutoThread(false)
                                  if (platform === 'Bluesky') setBlueskyAutoThread(false)
                                  if (platform === 'Threads') setThreadsAutoThread(false)
                                }
                              })
                            }
                          }}>
                          Use
                        </button>
                        <button className="smm-btn smm-btn-sm"
                          disabled={aiSelectedIdxs.size === 0 || draftsSaved}
                          onClick={async () => {
                            const selected = Array.from(aiSelectedIdxs).map(i => aiSuggestions[i])
                            const platforms = connections.filter(c => selectedConnIds.includes(c.connectionID)).map(c => c.platformName)
                            try {
                              await api.post(`/workspaces/${wsId}/posts/drafts/bulk`, {
                                drafts: selected.map(s => ({ contentText: s.text, angle: s.angle, source: 'ai-suggest' })),
                                aiPrompt: aiPrompt,
                                targetPlatforms: platforms.join(','),
                              })
                              setDraftsSaved(true)
                            } catch (err) { console.error('[SMM] save drafts', err) }
                          }}>
                          {draftsSaved ? 'Saved!' : 'Save to Drafts'}
                        </button>
                        <button className="smm-btn smm-btn-sm"
                          onClick={() => { setAiSuggestions([]); setAiSelectedIdxs(new Set()); setDraftsSaved(false) }}
                          style={{ marginLeft: 'auto' }}>
                          Clear
                        </button>
                      </div>
                      <div style={{ display: 'grid', gap: '0.35rem' }}>
                        {aiSuggestions.map((s, i) => (
                          <div key={i} style={{
                            display: 'flex', gap: '0.5rem', padding: '0.5rem 0.75rem',
                            background: aiSelectedIdxs.has(i) ? '#eef6ff' : '#f9f9f9',
                            borderRadius: '6px', border: '1px solid #e0e0e0', alignItems: 'flex-start',
                          }}>
                            <input type="checkbox" checked={aiSelectedIdxs.has(i)}
                              onChange={() => setAiSelectedIdxs(prev => {
                                const next = new Set(prev)
                                next.has(i) ? next.delete(i) : next.add(i)
                                return next
                              })}
                              style={{ marginTop: '0.25rem', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              {s.angle && <span style={{ fontSize: '0.7rem', background: '#e0e7ff', color: '#4338ca', padding: '0.1rem 0.4rem', borderRadius: 4, marginBottom: 4, display: 'inline-block' }}>{s.angle}</span>}
                              <p style={{ margin: 0, fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{s.text}</p>
                            </div>
                            <button className="smm-btn smm-btn-sm smm-btn-danger"
                              onClick={() => { setAiSuggestions(prev => prev.filter((_, j) => j !== i)); setAiSelectedIdxs(prev => { const n = new Set(prev); n.delete(i); return n }) }}
                              style={{ padding: '0.1rem 0.4rem', flexShrink: 0 }}
                              title="Remove">X</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: attachedMedia.length > 0 ? '0.5rem' : 0 }}>
                <button type="button" className="smm-btn smm-btn-sm" onClick={() => {
                  setShowMediaPicker(true)
                  setMediaPickerLoading(true)
                  api.get<{ mediaID: number; fileName: string; mimeType: string; blobUrl: string; thumbnailUrl?: string }[]>(
                    `/workspaces/${wsId}/media?pageSize=50&page=1&mime=image`
                  ).then(assets => setMediaPickerAssets(assets))
                    .catch(() => setMediaPickerAssets([]))
                    .finally(() => setMediaPickerLoading(false))
                }}>
                  Attach from Media Library
                </button>
                <input ref={mediaInputRef} type="file" multiple accept="image/*,video/*" style={{ display: 'none' }}
                  onChange={e => {
                    const files = e.target.files
                    if (!files) return
                    Array.from(files).forEach(file => {
                      const reader = new FileReader()
                      reader.onload = () => {
                        setAttachedMedia(prev => [...prev, {
                          mediaID: Date.now() + Math.random(), fileName: file.name,
                          blobUrl: reader.result as string, mimeType: file.type,
                        }])
                      }
                      reader.readAsDataURL(file)
                    })
                    e.target.value = ''
                  }} />
                <button type="button" className="smm-btn smm-btn-sm" onClick={() => mediaInputRef.current?.click()}>
                  Upload New
                </button>
                {attachedMedia.length > 0 && <span className="smm-hint">{attachedMedia.length} file(s) attached</span>}
              </div>
              {attachedMedia.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {attachedMedia.map((m, i) => (
                    <div key={m.mediaID} style={{ position: 'relative', width: 80, height: 80 }}>
                      {m.mimeType.startsWith('image') ? (
                        <img src={m.blobUrl} alt={m.fileName}
                          onClick={() => setMediaDetailIdx(i)}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer' }} />
                      ) : m.mimeType.startsWith('video') ? (
                        <div onClick={() => setMediaDetailIdx(i)}
                          style={{ width: 80, height: 80, borderRadius: 6, border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e', color: '#fff', fontSize: '1.2rem', cursor: 'pointer' }}>
                          &#9654;
                        </div>
                      ) : (
                        <div onClick={() => setMediaDetailIdx(i)}
                          style={{ width: 80, height: 80, borderRadius: 6, border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', fontSize: '0.7rem', textAlign: 'center', padding: 4, cursor: 'pointer' }}>
                          {m.fileName.slice(0, 15)}
                        </div>
                      )}
                      <button onClick={() => setAttachedMedia(prev => prev.filter((_, j) => j !== i))}
                        style={{ position: 'absolute', top: -4, right: -4, background: '#e01b24', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: '0.65rem', cursor: 'pointer', lineHeight: 1 }}>x</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {attachedMedia.length > 1 && selectedConnIds.length > 0 && (() => {
              const imageCount = attachedMedia.filter(m => m.mimeType.startsWith('image')).length
              const warnings = selectedConnIds.map(id => {
                const conn = connections.find(c => c.connectionID === id)
                if (!conn?.platformName) return null
                const limit = PLATFORM_IMAGE_LIMITS[conn.platformName] || 10
                if (imageCount > limit) return { platform: conn.platformName, limit }
                return null
              }).filter(Boolean) as { platform: string; limit: number }[]
              if (warnings.length === 0) return null
              return (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  {warnings.map(w => (
                    <div key={w.platform}>{w.platform} allows max {w.limit} images -- post may be split or images dropped</div>
                  ))}
                </div>
              )
            })()}
            <div className="smm-char-counts">
              {(() => {
                const selectedPlatforms = new Set(
                  connections.filter(c => selectedConnIds.includes(c.connectionID) && c.platformName).map(c => c.platformName!)
                )
                return settingsTabs
                  .filter(tab => selectedPlatforms.has(tab) && PLATFORM_CHAR_LIMITS[tab])
                  .map(tab => {
                    const limit = PLATFORM_CHAR_LIMITS[tab]
                    const cc = customContentMap[tab]
                    const activeText = (cc?.enabled && cc.text) ? cc.text : contentText
                    const over = activeText.length > limit
                    return <span key={tab} className={over ? 'smm-char-over' : 'smm-char-ok'}>{tab}: {activeText.length}/{limit}</span>
                  })
              })()}
            </div>
            {settingsTabs.length > 0 && (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginTop: '0.5rem', overflow: 'hidden' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', overflowX: 'auto' }}>
                  {settingsTabs.map(tab => (
                    <button key={tab} type="button" onClick={() => setActiveSettingsTab(tab)}
                      style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', fontWeight: activeSettingsTab === tab ? 600 : 400, border: 'none', background: activeSettingsTab === tab ? '#fff' : 'transparent', borderBottom: activeSettingsTab === tab ? '2px solid #4361ee' : '2px solid transparent', cursor: 'pointer', whiteSpace: 'nowrap', color: activeSettingsTab === tab ? '#1e293b' : '#64748b' }}>
                      {tab}
                    </button>
                  ))}
                </div>
                <div style={{ padding: '0.75rem' }}>
                  {activeSettingsTab === 'Instagram' && (
                    <div>
                      <strong style={{ fontSize: '0.85rem' }}>Format</strong>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                          <input type="radio" name="igFormat" checked={instagramFormat === 'feed'} onChange={() => setInstagramFormat('feed')} />
                          Feed / Reel
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                          <input type="radio" name="igFormat" checked={instagramFormat === 'story'} onChange={() => setInstagramFormat('story')} />
                          Story
                        </label>
                      </div>
                      {instagramFormat === 'story' && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>
                            Highlight ID (optional - adds Story to a Highlight album to persist beyond 24h)
                          </label>
                          <input type="text" className="smm-input" placeholder="Leave blank for normal Story..."
                            value={instagramHighlightId} onChange={e => setInstagramHighlightId(e.target.value)}
                            style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: '0.85rem' }} />
                          <div className="smm-hint" style={{ fontSize: '0.72rem', marginTop: '0.2rem' }}>
                            Stories expire after 24 hours unless added to a Highlight. Provide the Highlight album ID to preserve.
                          </div>
                        </div>
                      )}
                      <div style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #f0f0f0' }}>
                        <strong style={{ fontSize: '0.82rem' }}>Post Settings</strong>
                        {selectedConnIds.map(id => {
                          const c = connections.find(x => x.connectionID === id)
                          if (!c || c.platformName !== 'Instagram') return null
                          return (
                            <label key={`disable-comments-${id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', padding: '0.3rem 0' }}>
                              <input type="checkbox"
                                checked={disableCommentsFor.has(c.platformName!)}
                                onChange={e => {
                                  setDisableCommentsFor(prev => {
                                    const next = new Set(prev)
                                    if (e.target.checked) next.add(c.platformName!)
                                    else next.delete(c.platformName!)
                                    return next
                                  })
                                }} />
                              Disable comments ({c.displayName})
                            </label>
                          )
                        })}
                      </div>
                      {renderCustomContentBlock('Instagram')}
                    </div>
                  )}

                  {activeSettingsTab === 'YouTube' && (
                    <div>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                          <input type="radio" name="ytFormat" checked={youtubeFormat === 'long'} onChange={() => setYoutubeFormat('long')} />
                          Video (Long)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                          <input type="radio" name="ytFormat" checked={youtubeFormat === 'short'} onChange={() => setYoutubeFormat('short')} />
                          Short
                        </label>
                      </div>
                      <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>Video Title</label>
                      <input type="text" className="smm-input" placeholder="Leave blank to use first line of content..."
                        value={youtubeTitle} onChange={e => setYoutubeTitle(e.target.value)}
                        maxLength={100}
                        style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: '0.85rem', marginBottom: '0.5rem' }} />
                      {youtubeFormat === 'long' && (
                        <div>
                          <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>Custom Thumbnail</label>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <input ref={ytThumbRef} type="file" accept="image/*" style={{ display: 'none' }}
                              onChange={e => {
                                const f = e.target.files?.[0]
                                if (!f) return
                                const reader = new FileReader()
                                reader.onload = () => setYoutubeThumbnail({ file: f, preview: reader.result as string })
                                reader.readAsDataURL(f)
                                e.target.value = ''
                              }} />
                            <button type="button" className="smm-btn smm-btn-sm" onClick={() => ytThumbRef.current?.click()}>
                              {youtubeThumbnail ? 'Change Thumbnail' : 'Upload Thumbnail'}
                            </button>
                            {youtubeThumbnail && (
                              <>
                                <img src={youtubeThumbnail.preview} alt="thumb" style={{ width: 48, height: 28, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                                <button className="smm-btn smm-btn-sm smm-btn-danger" onClick={() => setYoutubeThumbnail(null)}
                                  style={{ padding: '0.1rem 0.3rem', fontSize: '0.7rem' }}>x</button>
                              </>
                            )}
                          </div>
                          <div className="smm-hint" style={{ fontSize: '0.7rem', marginTop: '0.2rem' }}>
                            {youtubeThumbnail ? 'Custom thumbnail will be uploaded with the video.' : 'No thumbnail selected -- YouTube will use the first frame.'}
                          </div>
                        </div>
                      )}
                      <div className="smm-hint" style={{ fontSize: '0.75rem', marginTop: '0.35rem' }}>
                        {youtubeFormat === 'short'
                          ? 'Shorts are vertical videos under 60 seconds. #Shorts will be added to the title automatically.'
                          : 'Content text becomes the video description.'}
                      </div>
                      {renderCustomContentBlock('YouTube')}
                    </div>
                  )}

                  {activeSettingsTab === 'Pinterest' && (
                    <div>
                      <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' }}>Board</label>
                      {boardError && (
                        <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginBottom: '0.5rem', padding: '0.4rem', background: '#fff3f3', borderRadius: '4px', border: '1px solid #ffcdd2' }}>
                          {boardError}
                        </div>
                      )}
                      <select
                        value={selectedBoardId}
                        onChange={e => setSelectedBoardId(e.target.value)}
                        className="smm-input"
                        style={{ width: '100%', padding: '0.4rem', fontSize: '0.85rem' }}
                        disabled={boardsLoading}
                      >
                        {boardsLoading && <option value="">Loading boards...</option>}
                        {!boardsLoading && pinterestBoards.length === 0 && !boardError && <option value="">No boards found</option>}
                        {!boardsLoading && pinterestBoards.length === 0 && boardError && <option value="">Could not load boards</option>}
                        {pinterestBoards.map(b => (
                          <option key={b.boardId} value={b.boardId}>{b.name} ({b.pinCount} pins)</option>
                        ))}
                      </select>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                        <input type="text" className="smm-input" placeholder="New board name..."
                          value={newBoardName} onChange={e => setNewBoardName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleCreateBoard()}
                          style={{ flex: 1, padding: '0.35rem 0.5rem', fontSize: '0.8rem' }} />
                        <button type="button" className="smm-btn smm-btn-sm smm-btn-primary"
                          disabled={creatingBoard || !newBoardName.trim()} onClick={handleCreateBoard}
                          style={{ whiteSpace: 'nowrap' }}>
                          {creatingBoard ? 'Creating...' : 'Create Board'}
                        </button>
                      </div>
                      <div className="smm-hint" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        Pinterest pins require at least one image. Content text becomes the pin description (max 500 chars).
                      </div>
                      {renderCustomContentBlock('Pinterest')}
                    </div>
                  )}

                  {activeSettingsTab === 'Telegram' && (
                    <div>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ fontSize: '0.8rem', marginRight: '0.25rem' }}>Parse mode:</label>
                          <select
                            value={telegramParseMode}
                            onChange={e => setTelegramParseMode(e.target.value as 'HTML' | 'Markdown' | 'None')}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', borderRadius: 4, border: '1px solid #ccc' }}
                          >
                            <option value="HTML">HTML</option>
                            <option value="Markdown">Markdown</option>
                            <option value="None">Plain text</option>
                          </select>
                        </div>
                        <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <input type="checkbox" checked={telegramDisablePreview} onChange={e => setTelegramDisablePreview(e.target.checked)} />
                          Disable link preview
                        </label>
                      </div>
                      {renderCustomContentBlock('Telegram')}
                    </div>
                  )}

                  {activeSettingsTab === 'TikTok' && (
                    <div>
                      {/* Posting as @handle with avatar */}
                      {(() => {
                        const ttConn = connections.find(c => selectedConnIds.includes(c.connectionID) && c.platformName === 'TikTok')
                        return ttConn ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', padding: '0.4rem 0.6rem', background: '#f8f9fa', borderRadius: 8, fontSize: '0.82rem' }}>
                            {tiktokCreatorInfo?.avatarUrl
                              ? <img src={tiktokCreatorInfo.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                              : <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', flexShrink: 0 }}>&#127909;</span>
                            }
                            <span>Posting as <strong>@{tiktokCreatorInfo?.username || ttConn.displayName?.replace(/^@/, '')}</strong></span>
                          </div>
                        ) : null
                      })()}

                      {/* Post mode radio: Post vs Publish */}
                      <div style={{ marginBottom: '0.75rem' }}>
                        <strong style={{ fontSize: '0.82rem', display: 'block', marginBottom: '0.35rem' }}>Post Mode</strong>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', marginBottom: '0.35rem', cursor: 'pointer' }}>
                          <input type="radio" name="tiktokPostMode" checked={tiktokPostMode === 'post'} onChange={() => setTiktokPostMode('post')} style={{ marginTop: '0.2rem' }} />
                          <div>
                            <div style={{ fontWeight: 600 }}>Upload</div>
                            <div style={{ fontSize: '0.72rem', color: '#666' }}>Send to TikTok inbox for review and additional editing before publishing</div>
                          </div>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                          <input type="radio" name="tiktokPostMode" checked={tiktokPostMode === 'publish'} onChange={() => setTiktokPostMode('publish')} style={{ marginTop: '0.2rem' }} />
                          <div>
                            <div style={{ fontWeight: 600 }}>Publish</div>
                            <div style={{ fontSize: '0.72rem', color: '#666' }}>Publish directly to TikTok as-is</div>
                          </div>
                        </label>
                      </div>

                      {/* Privacy Level radio buttons -- only shown when "Publish" is selected */}
                      {tiktokPostMode === 'publish' && (
                        <>
                          <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Privacy Level</label>
                          {!tiktokPrivacy && (
                            <div style={{ fontSize: '0.72rem', color: '#b45309', marginBottom: '0.4rem', padding: '0.3rem 0.5rem', background: '#fffbeb', borderRadius: 4 }}>
                              Select a privacy level to enable publishing.
                            </div>
                          )}
                          <div style={{ display: 'grid', gap: '0.25rem', marginBottom: '0.5rem' }}>
                            {(() => {
                              const DISPLAY_ORDER = ['PUBLIC_TO_EVERYONE', 'FOLLOWER_OF_CREATOR', 'MUTUAL_FOLLOW_FRIENDS', 'SELF_ONLY']
                              const apiOpts = tiktokCreatorInfo?.privacyLevelOptions ?? []
                              const ordered = apiOpts.length > 0
                                ? DISPLAY_ORDER.filter(o => apiOpts.includes(o))
                                : DISPLAY_ORDER
                              return ordered.map(opt => {
                              const blocked = tiktokBrandContent && opt === 'SELF_ONLY'
                              const label = opt === 'PUBLIC_TO_EVERYONE' ? 'Public (Everyone)' : opt === 'FOLLOWER_OF_CREATOR' ? 'Followers Only' : opt === 'MUTUAL_FOLLOW_FRIENDS' ? 'Friends (Mutual Followers)' : opt === 'SELF_ONLY' ? 'Private (Only Me)' : opt
                              return (
                                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', opacity: blocked ? 0.45 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}>
                                  <input type="radio" name="tiktokPrivacy" value={opt} checked={tiktokPrivacy === opt} disabled={blocked}
                                    onChange={() => setTiktokPrivacy(opt)} />
                                  {label}{blocked ? ' -- not allowed with Branded Content' : ''}
                                </label>
                              )
                            })
                            })()}
                          </div>
                          {tiktokBrandContent && tiktokPrivacy === 'SELF_ONLY' && (
                            <div style={{ fontSize: '0.75rem', color: '#dc2626', marginBottom: '0.5rem' }}>
                              Branded Content requires Public or Friends privacy. Switching to Public.
                            </div>
                          )}
                        </>
                      )}

                      {/* Interaction toggles */}
                      <div style={{ display: 'grid', gap: '0.3rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', opacity: tiktokCreatorInfo?.duetDisabled ? 0.45 : 1 }}>
                          <input type="checkbox"
                            checked={tiktokAllowDuet}
                            disabled={tiktokCreatorInfo?.duetDisabled === true}
                            onChange={e => setTiktokAllowDuet(e.target.checked)} />
                          Allow Duet{tiktokCreatorInfo?.duetDisabled ? ' (off at account level)' : ''}
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', opacity: tiktokCreatorInfo?.stitchDisabled ? 0.45 : 1 }}>
                          <input type="checkbox"
                            checked={tiktokAllowStitch}
                            disabled={tiktokCreatorInfo?.stitchDisabled === true}
                            onChange={e => setTiktokAllowStitch(e.target.checked)} />
                          Allow Stitch{tiktokCreatorInfo?.stitchDisabled ? ' (off at account level)' : ''}
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', opacity: tiktokCreatorInfo?.commentDisabled ? 0.45 : 1 }}>
                          <input type="checkbox"
                            checked={tiktokAllowComment}
                            disabled={tiktokCreatorInfo?.commentDisabled === true}
                            onChange={e => setTiktokAllowComment(e.target.checked)} />
                          Allow Comments{tiktokCreatorInfo?.commentDisabled ? ' (off at account level)' : ''}
                        </label>
                      </div>

                      {/* Video duration warning */}
                      {tiktokDurationExceeded && (
                        <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.6rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: '0.82rem', color: '#991b1b' }}>
                          This video is {tiktokVideoDuration}s -- this TikTok account's limit is {tiktokCreatorInfo?.maxVideoPostDurationSec}s. Trim it or choose a shorter clip.
                        </div>
                      )}

                      {/* Disclosure toggle */}
                      <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #f0f0f0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={tiktokDisclosureOn} onChange={e => {
                            setTiktokDisclosureOn(e.target.checked)
                            if (!e.target.checked) { setTiktokBrandContent(false); setTiktokBrandOrganic(false) }
                          }} />
                          <strong>Content Disclosure</strong>
                        </label>
                        <div style={{ fontSize: '0.72rem', color: '#666', marginTop: '0.15rem', paddingLeft: '1.7rem' }}>Turn on if this video promotes yourself, a brand, product, or service</div>
                        {tiktokDisclosureOn && (
                          <>
                            <div style={{ display: 'grid', gap: '0.3rem', marginTop: '0.35rem', paddingLeft: '1.5rem' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                                <input type="checkbox" checked={tiktokBrandContent} onChange={e => {
                                  if (e.target.checked && tiktokPostMode === 'publish') {
                                    setShowBrandedBlockModal(true)
                                    return
                                  }
                                  setTiktokBrandContent(e.target.checked)
                                }} />
                                Branded Content (paid partnership)
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                                <input type="checkbox" checked={tiktokBrandOrganic} onChange={e => setTiktokBrandOrganic(e.target.checked)} />
                                Your Brand (your own brand)
                              </label>
                            </div>
                            {tiktokBrandContent && (
                              <div className="smm-hint" style={{ fontSize: '0.72rem', marginTop: '0.3rem', paddingLeft: '1.5rem' }}>
                                Your video will be labeled as "Paid partnership".
                              </div>
                            )}
                            {tiktokBrandOrganic && !tiktokBrandContent && (
                              <div className="smm-hint" style={{ fontSize: '0.72rem', marginTop: '0.3rem', paddingLeft: '1.5rem' }}>
                                Your video will be labeled as "Promotional content".
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* AI-Generated Content disclosure */}
                      <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #f0f0f0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={tiktokIsAigc} onChange={e => setTiktokIsAigc(e.target.checked)} />
                          <strong>Label as AI-generated content</strong>
                        </label>
                        <div style={{ fontSize: '0.72rem', color: '#666', marginTop: '0.15rem', paddingLeft: '1.7rem' }}>
                          Turn this on if this video was made or significantly edited using AI. TikTok will show a "Creator labeled as AI-generated" tag on the post.
                        </div>
                      </div>

                      {renderCustomContentBlock('TikTok')}
                    </div>
                  )}

                  {activeSettingsTab === 'X/Twitter' && (
                    <div>
                      {(() => {
                        const xText = xEffectiveText
                        const xParts = xAutoThread && xText.length > 280 ? Math.ceil(xText.length / 280) : 1
                        const perPost = xCostEstimate?.estimatedCost ?? 0
                        const totalCost = perPost * xParts
                        return (
                          <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                              <input type="checkbox" checked={xAutoThread} onChange={e => {
                                setXAutoThread(e.target.checked)
                                if (e.target.checked) setCustomContentMap(prev => ({ ...prev, ['X/Twitter']: { ...prev['X/Twitter'], enabled: false, text: prev['X/Twitter']?.text ?? '' } }))
                              }} />
                              Auto-thread if over 280 characters
                            </label>
                            <div className="smm-hint" style={{ fontSize: '0.72rem', marginTop: '0.3rem' }}>
                              Content exceeding 280 characters will be automatically split into a threaded reply chain.
                            </div>
                            {xAutoThread && xText.length > 280 && (
                              <div style={{ marginTop: '0.4rem', padding: '0.4rem 0.6rem', background: '#f0f5ff', borderRadius: 4, fontSize: '0.82rem', border: '1px solid #c7d2fe' }}>
                                Will split into <strong>{xParts} posts</strong> ({xText.length} chars / 280 per post)
                              </div>
                            )}
                            {xCostEstimate && (
                              <div style={{ marginTop: '0.35rem', padding: '0.4rem 0.6rem', background: '#fffbeb', borderRadius: 4, fontSize: '0.82rem' }}>
                                Estimated cost: <strong>${totalCost.toFixed(4)}</strong>
                                {xParts > 1
                                  ? <> ({xParts} posts &times; ${perPost.toFixed(4)}/post)</>
                                  : <> (${perPost.toFixed(4)}/post)</>
                                }
                              </div>
                            )}
                          </>
                        )
                      })()}
                      {renderCustomContentBlock('X/Twitter')}
                    </div>
                  )}

                  {activeSettingsTab === 'Bluesky' && (
                    <div>
                      {(() => {
                        const ccB = customContentMap['Bluesky']
                        const bText = (ccB?.enabled && ccB.text) ? ccB.text : contentText
                        const bParts = blueskyAutoThread && bText.length > 300 ? Math.ceil(bText.length / 300) : 1
                        return (
                          <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                              <input type="checkbox" checked={blueskyAutoThread} onChange={e => {
                                setBlueskyAutoThread(e.target.checked)
                                if (e.target.checked) setCustomContentMap(prev => ({ ...prev, ['Bluesky']: { ...prev['Bluesky'], enabled: false, text: prev['Bluesky']?.text ?? '' } }))
                              }} />
                              Auto-thread if over 300 characters
                            </label>
                            <div className="smm-hint" style={{ fontSize: '0.72rem', marginTop: '0.3rem' }}>
                              Content exceeding 300 characters will be automatically split into a threaded reply chain.
                            </div>
                            {blueskyAutoThread && bText.length > 300 && (
                              <div style={{ marginTop: '0.4rem', padding: '0.4rem 0.6rem', background: '#f0f5ff', borderRadius: 4, fontSize: '0.82rem', border: '1px solid #c7d2fe' }}>
                                Will split into <strong>{bParts} posts</strong> ({bText.length} chars / 300 per post)
                              </div>
                            )}
                          </>
                        )
                      })()}
                      {renderCustomContentBlock('Bluesky')}
                    </div>
                  )}

                  {activeSettingsTab === 'Google Business' && (
                    <div>
                      <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: '0.25rem' }}>Post Type</label>
                      <select value={gbpPostType} onChange={e => setGbpPostType(e.target.value as 'STANDARD' | 'EVENT' | 'OFFER')}
                        className="smm-input" style={{ width: '100%', padding: '0.4rem', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                        <option value="STANDARD">Standard Update</option>
                        <option value="EVENT">Event</option>
                        <option value="OFFER">Offer / Discount</option>
                      </select>

                      {gbpPostType === 'EVENT' && (
                        <div style={{ display: 'grid', gap: '0.4rem', marginBottom: '0.5rem' }}>
                          <input type="text" placeholder="Event title" value={gbpEventTitle}
                            onChange={e => setGbpEventTitle(e.target.value)}
                            className="smm-input" style={{ padding: '0.4rem', fontSize: '0.85rem' }} />
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input type="datetime-local" value={gbpEventStartDate}
                              onChange={e => setGbpEventStartDate(e.target.value)}
                              className="smm-input" style={{ flex: 1, padding: '0.4rem', fontSize: '0.82rem' }} />
                            <input type="datetime-local" value={gbpEventEndDate}
                              onChange={e => setGbpEventEndDate(e.target.value)}
                              className="smm-input" style={{ flex: 1, padding: '0.4rem', fontSize: '0.82rem' }} />
                          </div>
                        </div>
                      )}

                      {gbpPostType === 'OFFER' && (
                        <div style={{ display: 'grid', gap: '0.4rem', marginBottom: '0.5rem' }}>
                          <input type="text" placeholder="Coupon code (optional)" value={gbpOfferCouponCode}
                            onChange={e => setGbpOfferCouponCode(e.target.value)}
                            className="smm-input" style={{ padding: '0.4rem', fontSize: '0.85rem' }} />
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input type="datetime-local" value={gbpEventStartDate} placeholder="Start date"
                              onChange={e => setGbpEventStartDate(e.target.value)}
                              className="smm-input" style={{ flex: 1, padding: '0.4rem', fontSize: '0.82rem' }} />
                            <input type="datetime-local" value={gbpEventEndDate} placeholder="End date"
                              onChange={e => setGbpEventEndDate(e.target.value)}
                              className="smm-input" style={{ flex: 1, padding: '0.4rem', fontSize: '0.82rem' }} />
                          </div>
                        </div>
                      )}

                      <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #f0f0f0' }}>
                        <strong style={{ fontSize: '0.8rem' }}>Call to Action</strong>
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                          <select value={gbpCtaType} onChange={e => setGbpCtaType(e.target.value)}
                            className="smm-input" style={{ padding: '0.4rem', fontSize: '0.82rem' }}>
                            <option value="LEARN_MORE">Learn More</option>
                            <option value="BOOK">Book</option>
                            <option value="ORDER">Order Online</option>
                            <option value="BUY">Buy</option>
                            <option value="SIGN_UP">Sign Up</option>
                            <option value="CALL">Call</option>
                          </select>
                          <input type="url" placeholder="CTA URL" value={gbpCtaUrl}
                            onChange={e => setGbpCtaUrl(e.target.value)}
                            className="smm-input" style={{ flex: 1, padding: '0.4rem', fontSize: '0.82rem' }} />
                        </div>
                      </div>
                      {renderCustomContentBlock('Google Business')}
                    </div>
                  )}

                  {activeSettingsTab === 'Discord' && (
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                        <input type="checkbox" checked={discordUseEmbed} onChange={e => setDiscordUseEmbed(e.target.checked)} />
                        Send as Rich Embed
                      </label>

                      {discordUseEmbed && (
                        <div style={{ display: 'grid', gap: '0.4rem', padding: '0.5rem', background: '#f8f9fa', borderRadius: 6 }}>
                          <input type="text" placeholder="Embed title" value={discordEmbedTitle}
                            onChange={e => setDiscordEmbedTitle(e.target.value)}
                            className="smm-input" style={{ padding: '0.4rem', fontSize: '0.85rem' }} />
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <label style={{ fontSize: '0.8rem' }}>Accent color:</label>
                            <input type="color" value={discordEmbedColor}
                              onChange={e => setDiscordEmbedColor(e.target.value)}
                              style={{ width: 32, height: 24, border: 'none', cursor: 'pointer' }} />
                          </div>
                          <input type="text" placeholder="Footer text (optional)" value={discordEmbedFooter}
                            onChange={e => setDiscordEmbedFooter(e.target.value)}
                            className="smm-input" style={{ padding: '0.4rem', fontSize: '0.85rem' }} />
                          <div className="smm-hint" style={{ fontSize: '0.72rem' }}>
                            The post body text will be used as the embed description. Attached images appear as the embed image.
                          </div>
                        </div>
                      )}

                      {!discordUseEmbed && (
                        <div className="smm-hint" style={{ fontSize: '0.72rem' }}>
                          Plain text message. Attached media URLs will be appended to the message (Discord auto-embeds them).
                        </div>
                      )}
                      {renderCustomContentBlock('Discord')}
                    </div>
                  )}

                  {activeSettingsTab === 'Threads' && (
                    <div>
                      {(() => {
                        const ccT = customContentMap['Threads']
                        const tText = (ccT?.enabled && ccT.text) ? ccT.text : contentText
                        const tParts = threadsAutoThread && tText.length > 500 ? Math.ceil(tText.length / 500) : 1
                        return (
                          <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                              <input type="checkbox" checked={threadsAutoThread} onChange={e => {
                                setThreadsAutoThread(e.target.checked)
                                if (e.target.checked) setCustomContentMap(prev => ({ ...prev, ['Threads']: { ...prev['Threads'], enabled: false, text: prev['Threads']?.text ?? '' } }))
                              }} />
                              Auto-thread if over 500 characters
                            </label>
                            <div className="smm-hint" style={{ fontSize: '0.72rem', marginTop: '0.3rem' }}>
                              Content exceeding 500 characters will be automatically split into a threaded reply chain.
                            </div>
                            {threadsAutoThread && tText.length > 500 && (
                              <div style={{ marginTop: '0.4rem', padding: '0.4rem 0.6rem', background: '#f0f5ff', borderRadius: 4, fontSize: '0.82rem', border: '1px solid #c7d2fe' }}>
                                Will split into <strong>{tParts} posts</strong> ({tText.length} chars / 500 per post)
                              </div>
                            )}
                          </>
                        )
                      })()}
                      {renderCustomContentBlock('Threads')}
                    </div>
                  )}

                  {activeSettingsTab && !['Instagram', 'YouTube', 'Pinterest', 'Telegram', 'TikTok', 'X/Twitter', 'Bluesky', 'Google Business', 'Discord', 'Threads'].includes(activeSettingsTab) && (
                    <div>
                      {renderCustomContentBlock(activeSettingsTab)}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="smm-schedule-row">
              <label><input type="checkbox" checked={!!scheduledAt} onChange={e => { if (!e.target.checked) setScheduledAt(''); else setScheduledAt(new Date(Date.now() + 3600000).toISOString().slice(0, 16)) }} /> Schedule for later</label>
              {scheduledAt && <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="smm-input" />}
            </div>
            {!scheduledAt && (
              <div className="smm-status-row">
                <label><input type="radio" name="status" checked={status === 'Draft'} onChange={() => setStatus('Draft')} /> Save as Draft</label>
              </div>
            )}
          </div>
          {error && <div className="smm-error">{error}</div>}
          {hasTikTokTarget && (
            <div style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.5, padding: '0.5rem 0', borderTop: '1px solid #f0f0f0' }}>
              {tiktokBrandContent
                ? <>By posting, you agree to TikTok's{' '}
                    <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5' }}>Branded Content Policy</a>
                    {' '}and{' '}
                    <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5' }}>Music Usage Confirmation</a>.
                  </>
                : <>By posting, you agree to TikTok's{' '}
                    <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5' }}>Music Usage Confirmation</a>.
                  </>
              }
            </div>
          )}
          {publishProgress && (
            <div style={{ background: '#eef6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: publishTargetProgress.length > 0 ? '0.5rem' : 0 }}>
                <div style={{ width: 18, height: 18, border: '2px solid #4361ee', borderTopColor: 'transparent', borderRadius: '50%', animation: 'smm-spin 0.8s linear infinite', flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1e40af' }}>{publishProgress}</span>
              </div>
              {publishTargetProgress.length > 0 && (
                <div style={{ display: 'grid', gap: '0.3rem' }}>
                  {publishTargetProgress.map((t, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', padding: '0.2rem 0' }}>
                      {t.status === 'Pending' && <span style={{ color: '#94a3b8' }}>&#9711;</span>}
                      {t.status === 'Published' && <span style={{ color: '#22c55e' }}>&#10003;</span>}
                      {t.status === 'Failed' && <span style={{ color: '#ef4444' }}>&#10005;</span>}
                      {t.status === 'Deleted' && <span style={{ color: '#f59e0b' }}>&#128465;</span>}
                      <span style={{ fontWeight: 500 }}>{t.platformName}</span>
                      <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{t.displayName}</span>
                      {t.status === 'Pending' && <span style={{ color: '#94a3b8', fontSize: '0.75rem', marginLeft: 'auto' }}>waiting...</span>}
                      {t.status === 'Published' && <span style={{ color: '#22c55e', fontSize: '0.75rem', marginLeft: 'auto' }}>done</span>}
                      {t.status === 'Failed' && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginLeft: 'auto' }}>{t.errorMessage ? t.errorMessage.slice(0, 60) + (t.errorMessage.length > 60 ? '...' : '') : 'failed'}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="smm-actions">
            <button onClick={handleSubmit} disabled={submitting} className="smm-btn">{scheduledAt ? 'Schedule Post' : 'Save Draft'}</button>
            <button onClick={handlePublishNow} disabled={submitting || tiktokDurationExceeded || (hasTikTokTarget && tiktokPostMode === 'publish' && !tiktokPrivacy)} className="smm-btn smm-btn-primary">
              {submitting ? 'Publishing...' : 'Publish Now'}
            </button>
          </div>
        </div>
        <div className="smm-composer-targets">
          <div className="smm-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Target Platforms</h3>
              <a href="/Social/connections" className="smm-hint" style={{ fontSize: '0.8rem' }}>Manage Conn.</a>
            </div>
            <div className="smm-targets-list" style={{ marginTop: '0.75rem' }}>
              {connections.filter(c => c.isActive).map(conn => {
                const isExpired = !!(conn.lastTokenError || (conn.tokenExpiresAt && new Date(conn.tokenExpiresAt) < new Date()))
                return (
                <label key={conn.connectionID} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0', borderBottom: '1px solid var(--smm-border, #e5e7eb)',
                  cursor: 'pointer',
                  background: isExpired ? '#fff3e0' : undefined,
                  borderLeft: isExpired ? '3px solid #ff9800' : undefined,
                  paddingLeft: isExpired ? '0.75rem' : undefined,
                }}>
                  <input type="checkbox" checked={selectedConnIds.includes(conn.connectionID)}
                    onChange={() => toggleConnection(conn.connectionID)}
                    style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <strong style={{ fontSize: '0.9rem' }}>{conn.platformName}</strong>
                      {isExpired && (
                        <button
                          type="button"
                          onClick={e => { e.preventDefault(); e.stopPropagation(); navigate(`/connections?reconnect=${encodeURIComponent(conn.platformName || '')}`) }}
                          style={{
                            fontSize: '0.7rem', color: '#d32f2f', fontWeight: 600,
                            background: '#ffebee', padding: '1px 6px', borderRadius: 4,
                            border: 'none', cursor: 'pointer', textDecoration: 'underline',
                          }}>
                          Reconnect
                        </button>
                      )}
                    </div>
                    <div className="smm-hint" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {conn.displayName} &middot; {conn.followerCount.toLocaleString()} followers
                    </div>
                    {selectedConnIds.includes(conn.connectionID) && attachedMedia.length > 0 && (() => {
                      const fw = getFormatWarnings(attachedMedia, conn.platformName ?? '')
                      if (fw.length === 0) return null
                      const allAutoConvert = fw.every(w => w.includes('auto-converted'))
                      return <div style={{ fontSize: '0.7rem', color: allAutoConvert ? '#92400e' : '#dc2626', fontWeight: 600, marginTop: 2 }}>{fw.join('; ')} <Link to="/info?tab=formats" style={{ color: '#2563eb', fontWeight: 400 }}>Details</Link></div>
                    })()}
                  </div>
                </label>
                )
              })}
            </div>
            {connections.length === 0 && <p className="smm-empty">No connections yet. <a href="/Social/connections">Connect a platform</a></p>}
            {selectedConnIds.length > 0 && (() => {
              const expiredSelected = selectedConnIds.filter(id => {
                const c = connections.find(x => x.connectionID === id)
                return c && (c.lastTokenError || (c.tokenExpiresAt && new Date(c.tokenExpiresAt) < new Date()))
              })
              return (
                <>
                  {expiredSelected.length > 0 && (
                    <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', background: '#ffebee', borderRadius: 6, fontSize: '0.8rem', color: '#c62828' }}>
                      {expiredSelected.length} expired connection{expiredSelected.length > 1 ? 's' : ''} selected -- publishing will fail.
                      {' '}<a href="/connections" style={{ color: '#1565c0', textDecoration: 'underline' }}>Reconnect</a>
                    </div>
                  )}
                  <div className="smm-hint" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                    {selectedConnIds.length} of {connections.filter(c => c.isActive).length} selected
                    {' '}<span style={{ margin: '0 4px', color: '#ccc' }}>|</span>{' '}
                    <Link to="/info?tab=formats" style={{ color: 'var(--smm-link, #2563eb)', textDecoration: 'none', fontSize: '0.78rem' }}>Format Support</Link>
                  </div>
                </>
              )
            })()}
          </div>
          <div className="smm-card">
            <h3
              className="smm-preview-toggle"
              onClick={() => setPreviewOpen(p => !p)}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              Per-Platform Preview
              <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#888' }}>{previewOpen ? '▲ collapse' : '▼ expand'}</span>
            </h3>
            {previewOpen && selectedConnIds.length === 0 && <p className="smm-empty">Select platforms to see previews</p>}
            {previewOpen && selectedConnIds.map(id => {
              const conn = connections.find(c => c.connectionID === id)
              if (!conn) return null
              const pn = conn.platformName ?? ''
              const limit = PLATFORM_CHAR_LIMITS[pn]
              const truncated = limit && contentText.length > limit ? contentText.slice(0, limit) + '...' : contentText
              const previewImages = attachedMedia.filter(m => m.mimeType.startsWith('image'))
              const maxImages = PLATFORM_IMAGE_LIMITS[pn] || 4
              const platformImages = previewImages.slice(0, maxImages)
              const displayText = truncated || 'Your post content here...'
              const dn = conn.displayName || conn.platformName || '?'
              const avatar = <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#ddd', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#666' }}>{dn[0].toUpperCase()}</div>
              const imgGrid = platformImages.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: platformImages.length === 1 ? '1fr' : 'repeat(2, 1fr)', gap: 4, borderRadius: 8, overflow: 'hidden' }}>
                  {platformImages.map((m, i) => <img key={m.mediaID} src={m.blobUrl} alt={m.fileName} style={{ width: '100%', height: platformImages.length === 1 ? 180 : 90, objectFit: 'cover', borderRadius: 4, ...(platformImages.length === 3 && i === 0 ? { gridRow: '1 / 3' } : {}) }} />)}
                </div>
              ) : null
              const formatWarnings = getFormatWarnings(attachedMedia, pn)
              const isThreadable = pn === 'X/Twitter' || pn === 'Bluesky' || pn === 'Threads'
              const threadingActive = (pn === 'X/Twitter' && xAutoThread) || (pn === 'Bluesky' && blueskyAutoThread) || (pn === 'Threads' && threadsAutoThread)
              const warnings = <>
                {truncated !== contentText && <span className="smm-warning">
                  {isThreadable
                    ? (threadingActive ? `Content will be auto-threaded on ${pn}` : `Content will be truncated or auto-threaded on ${pn}`)
                    : `Content will be truncated on ${pn}`}
                </span>}
                {previewImages.length > maxImages && <span style={{ fontSize: '0.75rem', color: '#f59e0b', display: 'block', marginTop: 4 }}>+{previewImages.length - maxImages} more images not shown ({pn} max: {maxImages})</span>}
                {formatWarnings.map((w, i) => {
                  const isAutoConvert = w.includes('auto-converted')
                  return <span key={i} style={{ fontSize: '0.75rem', color: isAutoConvert ? '#92400e' : '#dc2626', background: isAutoConvert ? '#fef3c7' : '#fef2f2', padding: '2px 6px', borderRadius: 4, display: 'block', marginTop: 4, fontWeight: 600 }}>{isAutoConvert ? '⟳' : '⚠'} {w}</span>
                })}
                {formatWarnings.length > 0 && <Link to="/info?tab=formats" style={{ fontSize: '0.7rem', color: '#2563eb', display: 'block', marginTop: 2 }}>View format compatibility</Link>}
              </>

              const renderByPlatform = () => {
                if (pn === 'X/Twitter') return (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                    {avatar}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{dn}</span>
                        <span style={{ fontSize: '0.75rem', color: '#71767b' }}>@{dn.replace(/\s/g, '').toLowerCase()} · now</span>
                      </div>
                      <p style={{ margin: '0.2rem 0 0', fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>{displayText}</p>
                      {imgGrid && <div style={{ marginTop: '0.5rem', borderRadius: 16, overflow: 'hidden' }}>{imgGrid}</div>}
                      <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', fontSize: '0.75rem', color: '#71767b' }}>
                        <span>Reply</span><span>Repost</span><span>Like</span><span>Share</span>
                      </div>
                    </div>
                  </div>
                )

                if (pn === 'Facebook') return (
                  <div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                      {avatar}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{dn}</div>
                        <div style={{ fontSize: '0.7rem', color: '#65676b' }}>Just now · Public</div>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{displayText}</p>
                    {imgGrid}
                    <div style={{ display: 'flex', justifyContent: 'space-around', borderTop: '1px solid #e4e6eb', marginTop: '0.5rem', paddingTop: '0.4rem', fontSize: '0.8rem', color: '#65676b' }}>
                      <span>Like</span><span>Comment</span><span>Share</span>
                    </div>
                  </div>
                )

                if (pn === 'Instagram') return (
                  <div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #f58529, #dd2a7b, #8134af)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, color: '#333' }}>{dn[0].toUpperCase()}</div>
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{dn}</span>
                    </div>
                    {imgGrid ? <div style={{ borderRadius: 0, overflow: 'hidden', margin: '0 -0.75rem' }}>{imgGrid}</div> : <div style={{ height: 180, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', margin: '0 -0.75rem' }}>No image attached</div>}
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                      <span>Heart</span><span>Comment</span><span>Share</span><span style={{ marginLeft: 'auto' }}>Save</span>
                    </div>
                    <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.3 }}><strong>{dn}</strong> {displayText}</p>
                  </div>
                )

                if (pn === 'Threads') return (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                    {avatar}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{dn}</span>
                        <span style={{ fontSize: '0.7rem', color: '#999' }}>now</span>
                      </div>
                      <p style={{ margin: '0.15rem 0', fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>{displayText}</p>
                      {imgGrid && <div style={{ marginTop: '0.4rem', borderRadius: 12, overflow: 'hidden' }}>{imgGrid}</div>}
                      <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem', fontSize: '0.75rem', color: '#999' }}>
                        <span>Heart</span><span>Reply</span><span>Repost</span><span>Share</span>
                      </div>
                    </div>
                  </div>
                )

                if (pn.startsWith('LinkedIn')) return (
                  <div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem' }}>
                      {avatar}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{dn}</div>
                        <div style={{ fontSize: '0.7rem', color: '#666' }}>Just now · Connections</div>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{displayText}</p>
                    {imgGrid}
                    <div style={{ display: 'flex', justifyContent: 'space-around', borderTop: '1px solid #e0e0e0', marginTop: '0.5rem', paddingTop: '0.4rem', fontSize: '0.8rem', color: '#666' }}>
                      <span>Like</span><span>Comment</span><span>Repost</span><span>Send</span>
                    </div>
                  </div>
                )

                if (pn === 'Bluesky') return (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                    {avatar}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{dn}</span>
                        <span style={{ fontSize: '0.75rem', color: '#888' }}>@{dn.replace(/\s/g, '').toLowerCase()} · now</span>
                      </div>
                      <p style={{ margin: '0.15rem 0 0', fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>{displayText}</p>
                      {imgGrid && <div style={{ marginTop: '0.4rem', borderRadius: 8, overflow: 'hidden' }}>{imgGrid}</div>}
                      <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem', fontSize: '0.75rem', color: '#888' }}>
                        <span>Reply</span><span>Repost</span><span>Like</span>
                      </div>
                    </div>
                  </div>
                )

                if (pn === 'Pinterest') return (
                  <div style={{ textAlign: 'center' }}>
                    {imgGrid ? <div style={{ borderRadius: 16, overflow: 'hidden', marginBottom: '0.5rem' }}>{imgGrid}</div> : <div style={{ height: 180, background: '#f0f0f0', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', marginBottom: '0.5rem' }}>Pin image required</div>}
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 2 }}>{displayText.slice(0, 100)}</div>
                    <div style={{ fontSize: '0.75rem', color: '#767676', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>{avatar}<span>{dn}</span></div>
                  </div>
                )

                if (pn === 'TikTok') return (
                  <div style={{ background: '#000', color: '#fff', borderRadius: 12, padding: '1rem 0.75rem', position: 'relative', minHeight: 200 }}>
                    {imgGrid ? <div style={{ position: 'absolute', inset: 0, borderRadius: 12, overflow: 'hidden', opacity: 0.4 }}>{imgGrid}</div> : null}
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: 160 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>@{dn.replace(/\s/g, '').toLowerCase()}</div>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.3 }}>{displayText.slice(0, 150)}</p>
                    </div>
                  </div>
                )

                if (pn === 'YouTube') {
                  const ytTitle = displayText.indexOf('\n') > 0 ? displayText.slice(0, displayText.indexOf('\n')) : displayText.slice(0, 100)
                  const ytDesc = displayText.indexOf('\n') > 0 ? displayText.slice(displayText.indexOf('\n') + 1) : displayText
                  return (
                    <div>
                      <div style={{ background: '#0f0f0f', borderRadius: 8, overflow: 'hidden', position: 'relative', marginBottom: '0.5rem' }}>
                        {imgGrid || <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '0.85rem' }}>Upload a video to preview</div>}
                        {youtubeFormat === 'short' && <span style={{ position: 'absolute', top: 6, right: 6, background: '#ff0000', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>SHORT</span>}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 2 }}>{ytTitle}</div>
                      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.75rem', color: '#606060' }}>
                        {avatar}
                        <span>{dn}</span>
                        <span style={{ marginLeft: '0.3rem' }}>0 views · Just now</span>
                      </div>
                      <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#606060', lineHeight: 1.3, maxHeight: 40, overflow: 'hidden' }}>{ytDesc}</p>
                    </div>
                  )
                }

                return (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                    {avatar}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: 2 }}>{dn}</div>
                      <p style={{ margin: 0, fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{displayText}</p>
                      {imgGrid && <div style={{ marginTop: '0.5rem' }}>{imgGrid}</div>}
                    </div>
                  </div>
                )
              }

              return (
                <div key={id} className="smm-preview-card">
                  <div className="smm-preview-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{pn}</span>
                    <span style={{ fontSize: '0.7rem', opacity: 0.7, fontWeight: 400 }}>{conn.displayName}</span>
                  </div>
                  <div className="smm-preview-body">
                    {renderByPlatform()}
                    {warnings}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {showFormatWarning && (
        <div onClick={() => setShowFormatWarning(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 100001, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, maxWidth: 420, width: '100%', padding: '1.5rem' }}>
            <h3 style={{ margin: '0 0 0.75rem', color: '#f59e0b' }}>Format Mismatch</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', lineHeight: 1.4 }}>{showFormatWarning}</p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="smm-btn" onClick={() => setShowFormatWarning(null)}>Go Back</button>
              <button className="smm-btn smm-btn-primary" onClick={() => { setShowFormatWarning(null); handlePublishNow() }}>
                Publish Anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {mediaDetailIdx !== null && attachedMedia[mediaDetailIdx] && (() => {
        const m = attachedMedia[mediaDetailIdx]
        const isImage = m.mimeType.startsWith('image')
        const isVideo = m.mimeType.startsWith('video')
        const handleUploadToGallery = async () => {
          if (uploadingToGallery) return
          setUploadingToGallery(true)
          try {
            const blob = await fetch(m.blobUrl).then(r => r.blob())
            const form = new FormData()
            form.append('file', blob, m.fileName)
            await api.upload(`/workspaces/${wsId}/media/upload`, form)
            alert('Uploaded to Media Library!')
          } catch (err) {
            console.error('[SMM] Gallery upload failed', err)
            alert('Upload failed -- see console for details.')
          } finally { setUploadingToGallery(false) }
        }
        return (
          <div onClick={() => setMediaDetailIdx(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                <strong style={{ fontSize: '0.9rem' }}>{m.fileName}</strong>
                <button onClick={() => setMediaDetailIdx(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }}>x</button>
              </div>
              <div style={{ padding: '1rem', textAlign: 'center' }}>
                {isImage && <img src={m.blobUrl} alt={m.fileName} style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 8 }} />}
                {isVideo && <video src={m.blobUrl} controls style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 8 }} />}
                {!isImage && !isVideo && <div style={{ padding: '2rem', color: '#666' }}>Preview not available for this file type</div>}
              </div>
              <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: '#888', marginRight: 'auto' }}>{m.mimeType}</span>
                <button className="smm-btn smm-btn-sm" onClick={handleUploadToGallery} disabled={uploadingToGallery}>
                  {uploadingToGallery ? 'Uploading...' : 'Save to Media Library'}
                </button>
                <button className="smm-btn smm-btn-sm smm-btn-danger" onClick={() => { setAttachedMedia(prev => prev.filter((_, j) => j !== mediaDetailIdx)); setMediaDetailIdx(null) }}>
                  Remove
                </button>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button className="smm-btn smm-btn-sm" disabled={mediaDetailIdx === 0} onClick={() => setMediaDetailIdx(mediaDetailIdx - 1)}>Prev</button>
                  <button className="smm-btn smm-btn-sm" disabled={mediaDetailIdx >= attachedMedia.length - 1} onClick={() => setMediaDetailIdx(mediaDetailIdx + 1)}>Next</button>
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {showMediaPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowMediaPicker(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
              <strong>Select from Media Library</strong>
              <button onClick={() => setShowMediaPicker(false)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#666' }}>x</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
              {mediaPickerLoading ? (
                <p className="smm-muted">Loading media...</p>
              ) : mediaPickerAssets.length === 0 ? (
                <p className="smm-muted">No media assets found. Upload some first.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem' }}>
                  {mediaPickerAssets.map(asset => {
                    const isSelected = attachedMedia.some(m => m.mediaID === asset.mediaID)
                    return (
                      <div key={asset.mediaID}
                        style={{ position: 'relative', cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
                          border: isSelected ? '3px solid #2563eb' : '2px solid #e5e7eb', opacity: isSelected ? 0.7 : 1 }}
                        onClick={() => {
                          if (isSelected) {
                            setAttachedMedia(prev => prev.filter(m => m.mediaID !== asset.mediaID))
                          } else {
                            setAttachedMedia(prev => [...prev, {
                              mediaID: asset.mediaID, fileName: asset.fileName,
                              blobUrl: asset.blobUrl || asset.thumbnailUrl || '', mimeType: asset.mimeType
                            }])
                          }
                        }}>
                        <img src={asset.thumbnailUrl || asset.blobUrl} alt={asset.fileName}
                          style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                        {isSelected && (
                          <div style={{ position: 'absolute', top: 4, right: 4, background: '#2563eb', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>&#10003;</div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="smm-hint">{attachedMedia.length} file(s) selected</span>
              <button className="smm-btn smm-btn-primary" onClick={() => setShowMediaPicker(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
